import { useState, useCallback, useEffect, useMemo, useRef, type PointerEvent as ReactPointerEvent } from 'react'
import { flushSync } from 'react-dom'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { DragDropProvider, DragOverlay, useDraggable } from '@dnd-kit/react'
import {
  ArrowLeft,
  Eye,
  Download,
  Pencil,
  Settings,
  ArrowRight,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Save,
  Loader2,
  Check,
  Plus,
  Rows3,
  GripVertical,
  X,
  Library,
  Search,
  Upload,
  FileSpreadsheet,
  FileJson,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBackNav } from '@/components/navigation/BackLink'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FieldPalette } from '@/components/form-builder/FieldPalette'
import { FormCanvas } from '@/components/form-builder/FormCanvas'
import { CanvasErrorBoundary } from '@/components/form-builder/CanvasErrorBoundary'
import {
  applyFieldMove,
  applyOptionMoveInSections,
  isOptionRowTarget,
  restoreFieldOptions,
  isFieldTarget,
  placeFieldByTarget,
  reorderSections,
  resolveSectionId,
  sameFieldLayout,
} from '@/lib/formBuilderDnd'
import { isSortable } from '@dnd-kit/react/sortable'
import { PropertiesPanel } from '@/components/form-builder/PropertiesPanel'
import type { FormField, FormSection, FormMeta, FieldType, SelectOption, VisibilityRule, NotificationRule } from '@/components/form-builder/types'
import {
  FIELD_ICONS,
  FIELD_LABELS,
  createFieldFromLibrary,
  toBuilderFieldType,
} from '@/components/form-builder/field-config'
import { useFieldLibraryList, useProcessStepOptions } from '@/hooks/useFieldLibrary'
import { useFieldCategoriesList } from '@/hooks/useFieldCategories'
import { useCrossFormAuthoring, type CrossFormAuthoring } from '@/hooks/useCrossFormAuthoring'
import { fieldCategoriesApi } from '@/api/fieldCategoriesApi'
import { fieldLibraryApi, type FieldLibraryListItem } from '@/api/fieldLibraryApi'
import { processStepsApi } from '@/api/processStepsApi'
import { useTenant } from '@/providers/TenantProvider'
import {
  conflictingProcessNames,
  processFieldIds,
  removedProcessFieldIds,
  stampLibraryFieldIds,
  unnamedProcessFields,
  unregisteredProcessFields,
} from '@/lib/processFieldLibrary'
import { parseFormWorkbook } from '@/lib/formExcel'
import { anchorAllEligibleFields, anchorEligible } from '@/lib/formAnchoring'
import { downloadFormTemplate } from '@/lib/formTemplateFile'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { useAuth } from '@/providers/AuthProvider'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formUniqueId, versionLabel } from '@/lib/versioning'
import { formVersionIndicatorState } from '@/lib/formVersionIndicator'
import { FormVersionIndicator } from '@/components/form-builder/FormVersionIndicator'
import { FormLifecycleBadge } from '@/components/form-builder/FormLifecycleBadge'
import { extractFormulaRefs } from '@/lib/formula'
import { calculatedCellFormulas } from '@/lib/tableForm'
import { isCrossFormRef, suggestFieldId, suggestFormHandle, normalizeFormHandle, FORM_HANDLE_RE } from '@/lib/crossForm'
import { templateLabel } from '@/lib/crossFormTemplate'
import {
  buildFormExportFile,
  formExportFileName,
  referencedOptionSetIds,
  type FormExportOptionSet,
} from '@/lib/formTransfer'
import {
  FormDefinitionPanel,
  toSectionDefinitions,
} from '@/components/form-builder/FormDefinitionPanel'
import { ImportFormDialog } from '@/components/form-builder/ImportFormDialog'
import { makeDefaultTableConfig } from '@/components/form-builder/TableConfigBuilder'
import { FormRenderer } from '@/components/renderer/FormRenderer'
import { formsApi, isNewVersionRequired } from '@/api/formsApi'
import { optionSetsApi } from '@/api/optionSetsApi'
import {
  getSections,
  prerequisiteIds,
  sectionPrerequisiteIds,
  visibilitySourceIds,
  notificationSourceIds,
  notificationRules,
  wouldCreateVisibilityCycle,
  findFieldDependents,
  DEPENDENT_SURFACE_LABELS,
  type FieldDependent,
} from '@/lib/formSections'
import { operatorsFor, type VisibilityPickTarget } from '@/components/form-builder/VisibilityBuilder'
import type { NotificationPickTarget } from '@/components/form-builder/NotificationRuleBuilder'
import {
  FORM_CATEGORY_LABELS,
  FORM_CATEGORY_COLORS,
  FORM_CATEGORY_OPTIONS,
  type FormDefinition,
  type FieldDefinition,
  type FormCategory,
  type FormSubmission,
  type CgtFieldType,
  type FormulaGate,
  type FormFieldLayout,
} from '@/types/cgt'
import { sanitizeFieldLayout } from '@/lib/fieldLayout'
import { FORM_PAGE_GUTTER_CLASS } from '@/lib/pageLayout'
import { FormActionChecklist } from '@/components/form-builder/FormActionChecklist'
import { OperandSuggestionProvider } from '@/components/form-builder/operandSuggestions'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useTenantSigners } from '@/hooks/useSigners'

// Alphabetical by label, like every other form-type picker (z8nrz7ewg9).
const CATEGORY_OPTIONS = FORM_CATEGORY_OPTIONS

// Preview signs as a user in every group so signature fields are exercisable.
const PREVIEW_USER = { id: 'preview', name: 'Preview User', groups: ['MedicalDirector', 'LabTechnician'] }

/** A throwaway draft submission for the builder preview — never persisted. */
function makePreviewSubmission(): FormSubmission {
  const now = new Date().toISOString()
  return {
    id: 'preview',
    formDefinitionId: 'preview',
    patientMrn: 'PREVIEW',
    status: 'draft',
    values: {},
    createdAt: now,
    updatedAt: now,
  }
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Every field id already on the form — passed to the id minters so a new
 *  readable id (label + 3 random chars) can never collide with an existing one. */
function allFieldIds(sections: FormSection[]): Set<string> {
  return new Set(sections.flatMap(s => s.fields.map(f => f.id)))
}

function createField(type: FieldType, usedIds?: Set<string>): FormField {
  return {
    id: suggestFieldId(FIELD_LABELS[type], usedIds),
    type,
    label: FIELD_LABELS[type],
    required: false,
    placeholder: '',
    helpText: '',
    options:
      type === 'select'
        ? [
            { id: crypto.randomUUID(), label: 'Option 1' },
            { id: crypto.randomUUID(), label: 'Option 2' },
          ]
        : [],
    fieldWidth: 'full' as const,
    tableConfig: type === 'table' ? makeDefaultTableConfig() : undefined,
    constantText: type === 'description' ? '' : undefined,
  }
}

function createSection(title: string): FormSection {
  return { id: crypto.randomUUID(), title, width: 'full', fields: [] }
}

/** Maps a saved CGT FieldDefinition back to a builder FormField. */
function fieldDefToFormField(f: FieldDefinition): FormField {
  return {
    id: f.id,
    type: toBuilderFieldType(f.fieldType),
    label: f.label,
    required: f.required ?? false,
    placeholder: '',
    helpText: f.helpText ?? '',
    options: (f.options ?? []).map(o => ({ id: crypto.randomUUID(), label: o })),
    optionsSource: f.optionSetId ? 'optionSet' : 'inline',
    optionSetId: f.optionSetId,
    colSpan: (f.colSpan ?? 2) as 1 | 2,
    fieldWidth: f.fieldWidth,
    labelPosition: f.labelPosition,
    labelWidth: f.labelWidth,
    formula: f.formula,
    displayFormat: f.displayFormat,
    requiredGroup: f.requiredGroup,
    allowedSignerGroupIds: f.allowedSignerGroupIds,
    distinctSignerSet: f.distinctSignerSet,
    isGate: f.isGate,
    gatedFormType: f.gatedFormType,
    anchored: f.anchored,
    lisStage: f.lisStage,
    lisTestCode: f.lisTestCode,
    canChange: f.canChange,
    isMrField: f.isMrField,
    mrFieldKey: f.mrFieldKey,
    tableConfig: f.tableConfig,
    // Provenance — preserved on reload so re-saving keeps the field library link.
    libraryFieldId: f.libraryFieldId,
    unlockedBy: f.unlockedBy,
    visibility: f.visibility,
    // Normalized to the list shape on load, so everything downstream — the
    // builder, the pruner, the save — sees one shape and the legacy single
    // rule stops travelling any further than this line.
    notifications: notificationRules(f),
    attachment: f.attachment,
    constantText: f.constantText,
    unlockGate: f.unlockGate,
    signatureGate: f.signatureGate,
    oos: f.oos,
  }
}

/**
 * After any reorder, drops each field's `unlockedBy` prerequisites that are
 * missing or no longer strictly earlier in the flat field order. Prevents
 * forward references and deadlock cycles; a field with no valid prerequisites
 * left becomes unlocked.
 */
function pruneUnlockRefs(sections: FormSection[]): FormSection[] {
  const flat = sections.flatMap(s => s.fields)
  const idxById = new Map(flat.map((f, i) => [f.id, i]))
  return sections.map(s => ({
    ...s,
    fields: s.fields.map(f => {
      const ids = prerequisiteIds(f)
      if (ids.length === 0) return f
      const ownIdx = idxById.get(f.id)!
      // Keep only prerequisites that exist and sit strictly before this field.
      const kept = ids.filter(pid => {
        const ti = idxById.get(pid)
        return ti !== undefined && ti < ownIdx
      })
      if (kept.length === ids.length) return f
      return { ...f, unlockedBy: kept.length ? kept : undefined }
    }),
  }))
}

/**
 * Section-level mirror of pruneUnlockRefs. After any section reorder (or a
 * section delete), drops each section's `unlockedBySections` prerequisites that
 * are missing or no longer strictly earlier in section order. Prevents forward
 * references and deadlocks; a section left with no valid prerequisites becomes
 * ungated.
 */
function pruneSectionUnlockRefs(sections: FormSection[]): FormSection[] {
  const idxById = new Map(sections.map((s, i) => [s.id, i]))
  return sections.map((s, ownIdx) => {
    const ids = sectionPrerequisiteIds(s)
    if (ids.length === 0) return s
    // Keep only prerequisites that exist and sit strictly before this section.
    const kept = ids.filter(pid => {
      const ti = idxById.get(pid)
      return ti !== undefined && ti < ownIdx
    })
    if (kept.length === ids.length) return s
    return { ...s, unlockedBySections: kept.length ? kept : undefined }
  })
}

/**
 * Drops visibility conditions whose source field no longer exists (after a
 * field delete), and — for section rules — conditions whose source was moved
 * INSIDE the watching section (the backend hard-rejects that shape on save).
 * Half-edited rows (sourceFieldId '') are drafts awaiting a pick and are kept;
 * sanitizeVisibilityRule strips them at save time. Unlike unlockedBy,
 * visibility conditions may point at ANY value-bearing field — order doesn't
 * matter — so only existence/containment are checked. Groups left with no
 * conditions are dropped; a rule left with no groups is removed.
 *
 * Field notification rules share the identical {logic, groups} condition
 * tree and the same "any value-bearing field, order doesn't matter" sourcing
 * rule (no section containment restriction — notification is field-only), so
 * the same pruning runs over every rule in `f.notifications` too.
 */
function pruneVisibilityRefs(sections: FormSection[]): FormSection[] {
  const validIds = new Set(sections.flatMap(s => s.fields.map(f => f.id)))
  function pruneRule<T extends { groups: VisibilityRule['groups'] }>(
    rule: T | undefined,
    isValidSource: (id: string) => boolean,
  ): T | undefined {
    if (!rule) return undefined
    const groups = rule.groups
      .map(g => ({
        ...g,
        // Cross-form sources ({handle.fieldId}) live on ANOTHER form — they are
        // never dangling here and must survive same-form pruning. Unresolvable
        // ones surface as warnings in the builder/renderer instead.
        conditions: g.conditions.filter(
          c =>
            c.sourceFieldId === '' ||
            isCrossFormRef(c.sourceFieldId) ||
            isValidSource(c.sourceFieldId),
        ),
      }))
      .filter(g => g.conditions.length > 0)
    if (groups.length === 0) return undefined
    const kept = rule.groups.reduce((n, g) => n + g.conditions.length, 0)
    const now = groups.reduce((n, g) => n + g.conditions.length, 0)
    return kept === now ? rule : { ...rule, groups }
  }
  return sections.map(s => {
    const ownFieldIds = new Set(s.fields.map(f => f.id))
    const sectionRule = pruneRule(
      s.visibility,
      id => validIds.has(id) && !ownFieldIds.has(id),
    )
    const fields = s.fields.map(f => {
      const rule = pruneRule(f.visibility, id => validIds.has(id))
      // Per rule: one rule losing its last condition drops that rule alone,
      // never the field's other notifications.
      const before = notificationRules(f)
      const pruned = before
        .map(r => pruneRule(r, id => validIds.has(id)))
        .filter((r): r is NotificationRule => !!r)
      const notificationsChanged =
        pruned.length !== before.length || pruned.some((r, i) => r !== before[i])
      return rule === f.visibility && !notificationsChanged
        ? f
        : { ...f, visibility: rule, notifications: pruned }
    })
    return sectionRule === s.visibility && fields.every((f, i) => f === s.fields[i])
      ? s
      : { ...s, visibility: sectionRule, fields }
  })
}

// ─── Properties step ─────────────────────────────────────────────────────────

interface PropertiesStepProps {
  formMeta: FormMeta
  category: FormCategory
  allowedActions: string[]
  /** Paired with the Repeat action; null = does not repeat. */
  defaultRepeatCount?: number | null
  error: string | null
  onFormMetaChange: (patch: Partial<FormMeta>) => void
  onCategoryChange: (category: FormCategory) => void
  /** The count rides along when Repeat is toggled, so the pair stays atomic. */
  onAllowedActionsChange: (actions: string[], defaultRepeatCount?: number | null) => void
  onDefaultRepeatCountChange: (next: number | null) => void
  onContinue: () => void
  onDownloadTemplate: () => void
  onImportExcel: (file: File) => void
  /** Opens the JSON import dialog — the other way to create a form from a file. */
  onImportJson: () => void
  /**
   * Open the spreadsheet picker as soon as this step mounts. Set when the Form
   * Definitions Import menu sent the user here: that menu cannot run the Excel
   * import itself (it hydrates the builder with a draft to review), so it
   * navigates and the picker opens on arrival rather than making the user hunt
   * for the button it just promised.
   */
  autoOpenExcel?: boolean
  excelBusy: boolean
  excelErrors: string[]
  /** Field names the sheet asks for that aren't in the Field Library yet. */
  excelMissingFields: string[]
  /** Auto-suggested permanent handle (tracks the name until overridden). */
  suggestedHandle: string
  /** User override ('' = keep the suggestion). */
  handleOverride: string
  onHandleChange: (value: string) => void
}

function PropertiesStep({
  formMeta,
  category,
  allowedActions,
  defaultRepeatCount,
  error,
  onFormMetaChange,
  onCategoryChange,
  onAllowedActionsChange,
  onDefaultRepeatCountChange,
  onContinue,
  onDownloadTemplate,
  onImportExcel,
  onImportJson,
  autoOpenExcel = false,
  excelBusy,
  excelErrors,
  excelMissingFields,
  suggestedHandle,
  handleOverride,
  onHandleChange,
}: PropertiesStepProps) {
  const excelInputRef = useRef<HTMLInputElement>(null)
  const { can } = usePermission()

  // Mount-only, deliberately: re-running this would re-open the OS file dialog
  // on every render and make it impossible to cancel.
  useEffect(() => {
    if (autoOpenExcel) excelInputRef.current?.click()
  }, [autoOpenExcel])

  return (
    <div className='flex flex-1 items-start justify-center overflow-y-auto bg-muted/20 px-6 py-12'>
      <div className='w-full max-w-lg'>
        {/* Step indicator */}
        <div className='mb-6 flex items-center gap-2 text-xs text-muted-foreground'>
          <span className='flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground'>
            1
          </span>
          <span className='font-medium text-foreground'>Form Setup</span>
          <ChevronRight className='h-3.5 w-3.5' />
          <span className='flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold'>
            2
          </span>
          <span>Build Fields</span>
        </div>

        <div className='rounded-2xl border bg-card p-8 shadow-sm'>
          {/* Heading */}
          <div className='mb-6 flex items-center gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10'>
              <FileText className='h-5 w-5 text-primary' />
            </div>
            <div>
              <h2 className='text-lg font-semibold'>Form Properties</h2>
              <p className='text-sm text-muted-foreground'>
                Set up your form before adding fields.
              </p>
            </div>
          </div>

          <div className='space-y-5'>
            {/* Form Name */}
            <div className='space-y-1.5'>
              <Label>
                Form Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                value={formMeta.name}
                onChange={e => onFormMetaChange({ name: e.target.value })}
                placeholder='e.g. Patient Chart, Processing Form…'
                autoFocus
                onKeyDown={e => e.key === 'Enter' && onContinue()}
              />
            </div>

            {/* Permanent form handle — adjustable ONLY here, locked after creation */}
            <div className='space-y-1.5'>
              <Label>
                Form Handle{' '}
                <span className='text-xs font-normal text-muted-foreground'>
                  (permanent — set once)
                </span>
              </Label>
              <Input
                value={handleOverride || suggestedHandle}
                onChange={e => onHandleChange(e.target.value)}
                placeholder='e.g. patient-chart-x7k'
                className='font-mono text-sm'
              />
              <p className='text-[11px] text-muted-foreground'>
                Other forms reference this form's fields through this handle (e.g.{' '}
                <span className='font-mono'>{'{'}{handleOverride || suggestedHandle}.weight{'}'}</span>).
                Lowercase letters, numbers and hyphens. It can never be changed after the
                form is created — renaming the form won't affect it.
              </p>
            </div>

            {/* Description */}
            <div className='space-y-1.5'>
              <Label>
                Description{' '}
                <span className='text-xs font-normal text-muted-foreground'>(optional)</span>
              </Label>
              <Textarea
                value={formMeta.description}
                onChange={e => onFormMetaChange({ description: e.target.value })}
                placeholder='Briefly describe what this form collects…'
                rows={3}
                className='resize-none'
              />
            </div>

            <Separator />

            <div className='space-y-1.5'>
              <Label>Form Type</Label>
              <Select
                value={category}
                onValueChange={v => onCategoryChange(v as FormCategory)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(c => (
                    <SelectItem
                      key={c}
                      value={c}
                      style={{ backgroundColor: `${FORM_CATEGORY_COLORS[c]}1f` }}
                    >
                      <span className='flex items-center gap-2'>
                        <span
                          className='h-2.5 w-2.5 shrink-0 rounded-full'
                          style={{ backgroundColor: FORM_CATEGORY_COLORS[c] }}
                        />
                        {FORM_CATEGORY_LABELS[c]}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-muted-foreground'>
                Classifies the form. Order in a workflow is defined per procedure, not here.
              </p>
            </div>

            <Separator />

            {/* Actions checklist */}
            <div className='space-y-1.5'>
              <Label>Actions</Label>
              <FormActionChecklist
                value={allowedActions}
                onChange={onAllowedActionsChange}
                repeatCount={defaultRepeatCount}
                onRepeatCountChange={onDefaultRepeatCountChange}
              />
              <p className='text-xs text-muted-foreground'>
                Actions shown in the patient procedure action menu for this form.
              </p>
            </div>

            <Separator />

            <div className='flex items-start gap-2.5 rounded-lg border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground'>
              <Rows3 className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
              <span>
                Build the layout on the canvas with <span className='font-medium text-foreground'>sections</span>.
                Each section has a width (full, half, third, quarter) and holds its own fields —
                place narrow sections side by side to make multi-column rows.
              </span>
            </div>

            {error && <p className='text-sm text-destructive'>{error}</p>}

            <Button className='w-full' size='lg' onClick={onContinue}>
              Continue to Build Fields
              <ArrowRight className='ml-2 h-4 w-4' />
            </Button>

            {/* Both file on-ramps live in ONE card (z8nrz7ep1z). They are the
                same choice — start from a file instead of the blank canvas —
                and as two separate dashed boxes each opening with "Or" they
                read as unrelated ideas and pushed the primary action up off
                the screen. */}
            <div className='rounded-lg border border-dashed bg-muted/20'>
              <p className='border-b border-dashed px-4 py-2.5 text-sm font-medium'>
                Or start from a file
              </p>

              <div className='flex gap-3 p-4'>
                <FileJson className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
                <div className='min-w-0 flex-1 space-y-2'>
                  <p className='text-sm font-medium leading-none'>Form file</p>
                  <p className='text-xs text-muted-foreground'>
                    A <code className='rounded bg-muted px-1 py-0.5'>.json</code> file exported
                    from this builder. The form is built from the file, so the name and type you
                    set above are replaced by the ones it carries.
                  </p>
                  <Button variant='outline' size='sm' onClick={onImportJson}>
                    <Upload className='mr-2 h-4 w-4' />
                    Import form file
                  </Button>
                </div>
              </div>

              <div className='flex gap-3 border-t border-dashed p-4'>
                <FileSpreadsheet className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
                <div className='min-w-0 flex-1 space-y-2'>
                  <p className='text-sm font-medium leading-none'>Excel template</p>
                  <p className='text-xs text-muted-foreground'>
                    Download a template pre-filled with your Field Library, pick fields from the
                    dropdowns, then upload it. The form opens here for review before you save.
                  </p>
                  <div className='flex flex-wrap gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={onDownloadTemplate}
                      disabled={excelBusy}
                    >
                      <Download className='mr-2 h-4 w-4' />
                      Download template
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => excelInputRef.current?.click()}
                      disabled={excelBusy}
                    >
                      {excelBusy ? (
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      ) : (
                        <Upload className='mr-2 h-4 w-4' />
                      )}
                      Upload spreadsheet
                    </Button>
                    <input
                      ref={excelInputRef}
                      type='file'
                      accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                      className='hidden'
                      onChange={e => {
                        const file = e.target.files?.[0]
                        if (file) onImportExcel(file)
                        e.target.value = ''
                      }}
                    />
                  </div>
                  {/* Names the library doesn't have. One instruction covers the whole
                      set, so they get a message rather than an error per row — a form
                      for a new process can name dozens of them. */}
                  {excelMissingFields.length > 0 && (
                    <div className='space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3'>
                      <p className='text-xs font-medium text-destructive'>The form was not loaded.</p>
                      <p className='text-xs text-destructive'>
                        {excelMissingFields.length === 1
                          ? 'This field is not in the Field Library yet:'
                          : `These ${excelMissingFields.length} fields are not in the Field Library yet:`}
                      </p>
                      <ul
                        className={cn(
                          'list-inside list-disc text-xs font-medium text-destructive',
                          excelMissingFields.length > 6 &&
                            'max-h-48 overflow-y-auto sm:columns-2 sm:gap-4',
                        )}
                      >
                        {excelMissingFields.map(name => (
                          <li key={name} className='break-words'>
                            {name}
                          </li>
                        ))}
                      </ul>
                      <p className='text-xs text-destructive'>
                        Create them in the Field Library, then upload the file again. Nothing was
                        changed.
                      </p>
                      {can(PERMISSIONS.fieldsRead) && (
                        <Link
                          to='/form-builder?tab=fields'
                          className='inline-flex items-center gap-1 text-xs font-medium text-destructive underline underline-offset-2'
                        >
                          Open the Field Library
                          <ArrowRight className='h-3 w-3' />
                        </Link>
                      )}
                    </div>
                  )}
                  {excelErrors.length > 0 && (
                    <div className='space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3'>
                      <p className='text-xs font-medium text-destructive'>
                        Import failed — fix these and re-upload:
                      </p>
                      <ul className='list-inside list-disc space-y-0.5 text-xs text-destructive'>
                        {excelErrors.map((msg, i) => (
                          <li key={i}>{msg}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Panel resize / collapse helpers ─────────────────────────────────────────

const PALETTE_DEFAULT_W = 208  // w-52
const PALETTE_MIN_W = 160
const PALETTE_COLLAPSE_W = 120  // auto-collapse when dragged below this

const PROPS_DEFAULT_W = 288    // w-72
const PROPS_MIN_W = 224
const PROPS_COLLAPSE_W = 160   // auto-collapse when dragged below this
const PROPS_TABLE_W = 448      // w-[28rem] for table fields

function readLSNum(key: string): number | null {
  try { const v = localStorage.getItem(key); return v ? Number(v) : null } catch { return null }
}
function readLSBool(key: string): boolean {
  try { const v = localStorage.getItem(key); return v === 'true' } catch { return false }
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function FormEditorPage() {
  // The signer's display name for the audit row. The signature itself is the
  // password the dialog verifies — this is only what the trail shows.
  const { user, email } = useAuth()
  const signerName = user?.fullName ?? email ?? ''

  const { id: definitionId } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  // `?view=<id>` opens another version of this form read-only (z8nrz7dmb1 AC2).
  // Set and cleared on its own: the back-link origin rides in the same query.
  const [searchParams, setSearchParams] = useSearchParams()
  const setViewedVersion = useCallback(
    (id: string | null) =>
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        if (id) next.set('view', id)
        else next.delete('view')
        return next
      }),
    [setSearchParams],
  )
  // A version picked while there were unsaved changes, awaiting the prompt (AC2.7).
  const [pendingVersionId, setPendingVersionId] = useState<string | null>(null)
  // This toolbar link was hardcoded to the Forms tab. Arriving from the
  // sequence editor that is a DIFFERENT screen than the one you left, so it
  // follows `from` when a caller passed one (z8nrz7czdw).
  const backTo = useBackNav()
  const queryClient = useQueryClient()

  // Step: 'loading' while an existing definition is being fetched; 'properties' for new forms
  const [step, setStep] = useState<'loading' | 'properties' | 'builder'>(() =>
    definitionId ? 'loading' : 'properties',
  )

  const [formMeta, setFormMeta] = useState<FormMeta>({ name: '', description: '' })
  // New forms start with one full-width section to anchor the first group of fields.
  const [sections, setSections] = useState<FormSection[]>(() =>
    definitionId ? [] : [createSection('Section 1')],
  )
  // Pre-drag snapshot of `sections`, captured when a field drag starts. Field
  // moves are applied live in `handleDragOver`, so an Esc cancel has to rewind
  // to this rather than simply doing nothing at drop time. `sectionsRef` keeps
  // the latest layout readable from the drag handlers without giving them a
  // `sections` dependency — they'd otherwise re-register on every keystroke,
  // including mid-drag, since dragover itself sets state.
  const dragSnapshotRef = useRef<FormSection[] | null>(null)
  // Pre-drag options order for the field whose options list is being dragged,
  // so a canceled option drag restores rather than keeping the live preview
  // (AC1.4, AC1.5). Separate from dragSnapshotRef: an option drag never moves a
  // field, so snapshotting the whole tree would restore more than it should.
  const optionDragSnapshotRef = useRef<{ fieldId: string; options: SelectOption[] } | null>(null)
  const sectionsRef = useRef<FormSection[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  // Ephemeral submission the preview renderer fills in — never persisted.
  const [previewSubmission, setPreviewSubmission] = useState<FormSubmission>(makePreviewSubmission)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [propertiesError, setPropertiesError] = useState<string | null>(null)
  // Single source of truth for category + actions — the toolbar badge, toolbar
  // save, and the Form Settings panel all read/write this state.
  const [defMeta, setDefMeta] = useState<{
    category: FormCategory
    allowedActions: string[]
    // Paired with the `Repeat` action above — null means the form does not
    // repeat. Held here rather than derived so the editor can round-trip it
    // through save/load like every other setting on this panel.
    defaultRepeatCount?: number | null
    completionGate?: FormulaGate
    completerGroupIds?: string[]
    crossFormTemplateId?: string | null
    // Form-wide label default (z8nrz7djyx); null = the system default.
    fieldLayout?: FormFieldLayout | null
  }>({ category: 'clinical', allowedActions: [], defaultRepeatCount: null })
  const [saving, setSaving] = useState(false)
  const [justSaved, setJustSaved] = useState(false)
  const [defVersion, setDefVersion] = useState<number | null>(null)
  // The current version's user-facing label, when it carries one. Needed so
  // the "Save as New Version" dialog can require a label that differs from it.
  const [defVersionName, setDefVersionName] = useState<string | null>(null)
  // The open version's lifecycle status, for the version indicator (z8nrz7dmb1).
  // Null until read — the indicator then says "Version unavailable", never a guess.
  const [defActive, setDefActive] = useState<boolean | null>(null)
  // Bumped to re-read the saved definition: "Discard and continue" when the
  // author leaves unsaved changes to view another version (AC2.7).
  const [reloadKey, setReloadKey] = useState(0)
  // Lineage identity of the loaded definition: familyId locates this form's
  // step inside procedure templates (cross-form picker); handle is the
  // permanent id shown read-only in the header and echoed on save.
  const [defIdentity, setDefIdentity] = useState<{ familyId?: string; handle?: string }>({})
  // New-form handle: adjustable exactly once, at creation. '' = keep the
  // auto-suggestion (normalized name + a stable random 3-char suffix).
  const [newFormHandle, setNewFormHandle] = useState('')
  const [handleSuffix] = useState(() => suggestFormHandle('f').split('-').pop() ?? '000')
  const suggestedHandle = `${normalizeFormHandle(formMeta.name) || 'form'}-${handleSuffix}`
  const [versionDialogOpen, setVersionDialogOpen] = useState(false)
  // Import-from-JSON (z8nrz7ep1z) — always creates a new form, so it navigates
  // away rather than touching the form open here.
  const [importOpen, setImportOpen] = useState(false)
  // `?import=excel` from the Form Definitions Import menu. Read once and then
  // stripped from the URL, so returning to this step later — or sharing the
  // link — does not re-open the file picker.
  const [autoOpenExcel] = useState(() => searchParams.get('import') === 'excel')
  useEffect(() => {
    if (!autoOpenExcel) return
    const next = new URLSearchParams(searchParams)
    next.delete('import')
    setSearchParams(next, { replace: true })
    // Keyed on the one-shot flag, not on `searchParams` — that changes as the
    // user moves around the page and would re-run this needlessly.
  }, [autoOpenExcel])
  // Sign as roster for the version dialog — loaded only while it is open.
  const versionSigners = useTenantSigners(PERMISSIONS.formsEdit, versionDialogOpen)
  const [newVersionInput, setNewVersionInput] = useState('')
  const [versionError, setVersionError] = useState<string | null>(null)
  const { selectedTenantId, selectedTenant } = useTenant()
  // The processes this form carried as of its last load or save. Compared
  // against the canvas on save to spot which ones a removal would orphan; a ref
  // rather than state because nothing renders from it.
  const savedProcessIds = useRef<Set<string>>(new Set())
  // Non-null while the "you removed a process" confirmation is open.
  const [pendingRemovals, setPendingRemovals] = useState<string[] | null>(null)
  // Non-null while the "deleting this field breaks a formula" confirmation
  // (z8nrz7d0h6, Part 2) is open — set only when the field being deleted has
  // dependents; a field with none deletes immediately, same as today (AC2.5).
  const [pendingFieldDeletion, setPendingFieldDeletion] = useState<{
    id: string
    label: string
    dependents: FieldDependent[]
  } | null>(null)

  // Dirty-state tracking — JSON snapshot of the last-persisted form content.
  // null = new form (always dirty). Updated on load and on each successful save.
  const [savedJson, setSavedJson] = useState<string | null>(null)

  // null = unknown (loading or new form); true/false = result of in-use check
  const [inUse, setInUse] = useState<boolean | null>(null)

  // ── Panel resize / collapse ───────────────────────────────────────────────
  const [paletteW, setPaletteW] = useState(() => readLSNum('fb.palette.width') ?? PALETTE_DEFAULT_W)
  const [paletteCollapsed, setPaletteCollapsed] = useState(() => readLSBool('fb.palette.collapsed'))
  const [propsW, setPropsW] = useState<number | null>(() => readLSNum('fb.props.width'))
  const [propsCollapsed, setPropsCollapsed] = useState(() => readLSBool('fb.props.collapsed'))

  const paletteWRef = useRef(paletteW)
  paletteWRef.current = paletteW
  const propsWRef = useRef(propsW)
  propsWRef.current = propsW

  useEffect(() => { try { localStorage.setItem('fb.palette.width', String(paletteW)) } catch {} }, [paletteW])
  useEffect(() => { try { localStorage.setItem('fb.palette.collapsed', String(paletteCollapsed)) } catch {} }, [paletteCollapsed])
  useEffect(() => { try { if (propsW !== null) localStorage.setItem('fb.props.width', String(propsW)) } catch {} }, [propsW])
  useEffect(() => { try { localStorage.setItem('fb.props.collapsed', String(propsCollapsed)) } catch {} }, [propsCollapsed])

  const startPaletteResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startW = paletteWRef.current
    const onMove = (ev: PointerEvent) => {
      const next = startW + ev.clientX - startX
      if (next < PALETTE_COLLAPSE_W) {
        setPaletteCollapsed(true)
      } else {
        setPaletteCollapsed(false)
        setPaletteW(Math.min(Math.floor(window.innerWidth / 2), Math.max(PALETTE_MIN_W, next)))
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [])

  const startPropsResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startW = propsWRef.current ?? PROPS_DEFAULT_W
    const onMove = (ev: PointerEvent) => {
      // Left-edge handle: dragging left → wider panel
      const next = startW + startX - ev.clientX
      if (next < PROPS_COLLAPSE_W) {
        setPropsCollapsed(true)
      } else {
        setPropsCollapsed(false)
        setPropsW(Math.min(Math.floor(window.innerWidth / 2), Math.max(PROPS_MIN_W, next)))
      }
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [])

  const buildPayload = useCallback(
    // `overrideSections` lets a save use sections it has just derived, rather
    // than the state value, which React has not re-rendered with yet.
    (overrideSections?: FormSection[]) => ({
      name: formMeta.name.trim(),
      // The permanent handle: chosen once at creation (new forms), echoed
      // unchanged on later saves — the backend rejects any change.
      handle: definitionId ? defIdentity.handle : newFormHandle || suggestedHandle,
      category: defMeta.category,
      allowedActions: defMeta.allowedActions,
      defaultRepeatCount: defMeta.defaultRepeatCount ?? null,
      // Explicit null, like fieldLayout below: turning the gate off must SEND
      // the cleared value, because an absent key keeps the stored gate.
      completionGate: defMeta.completionGate ?? null,
      completerGroupIds: defMeta.completerGroupIds ?? [],
      // Explicit null clears a default that was removed; absent would keep it.
      fieldLayout: defMeta.fieldLayout ?? null,
      sections: toSectionDefinitions(overrideSections ?? sections),
    }),
    [formMeta.name, defMeta.category, defMeta.allowedActions, defMeta.defaultRepeatCount, defMeta.completionGate, defMeta.completerGroupIds, defMeta.fieldLayout, sections, definitionId, defIdentity.handle, newFormHandle, suggestedHandle],
  )

  // Snapshot of current editable state — recomputed only when content changes.
  const currentJson = useMemo(
    () => JSON.stringify({ name: formMeta.name.trim(), category: defMeta.category, allowedActions: defMeta.allowedActions, defaultRepeatCount: defMeta.defaultRepeatCount ?? null, completionGate: defMeta.completionGate, completerGroupIds: defMeta.completerGroupIds ?? [], fieldLayout: defMeta.fieldLayout ?? null, sections }),
    [formMeta.name, defMeta.category, defMeta.allowedActions, defMeta.defaultRepeatCount, defMeta.completionGate, defMeta.completerGroupIds, defMeta.fieldLayout, sections],
  )
  // Dirty when savedJson is null (new form never saved) OR content differs from last save.
  const isDirty = savedJson === null || currentJson !== savedJson

  /**
   * Give palette-dropped Process fields the provenance the library path stamps
   * for free.
   *
   * A Process field dropped from the "Special" group is created by
   * `createField` with a fresh uuid and no `libraryFieldId`, so it has no
   * Field Library row behind it. That leaves it unreachable: the Process page
   * enumerates candidates from the library, and `ProcessStepField` resolves its
   * config through `libraryFieldId ?? id`. Without a row the field can never be
   * configured and renders as an unconfigured process forever.
   *
   * Creating the row here — the same call the Process page's picker makes —
   * turns it into an ordinary library-backed Process field from its first save.
   * It deliberately does NOT create the ProcessStepConnection: an empty
   * connection would make the renderer treat the step as configured and offer
   * Initiate on a process that does nothing. The connection is created when
   * someone actually configures the step.
   *
   * Returns the sections to save with, since the state update has not landed by
   * the time the caller builds its payload.
   */
  /**
   * Anchor every Process field on the form to a connection carrying this
   * definition's id — that `formDefinitionId` is what records which processes
   * belong to this form, and so what a later removal is entitled to clean up.
   *
   * Runs AFTER the definition is saved, because a brand-new form has no id
   * until then. Get-or-create, so an already-configured process is untouched.
   *
   * Best-effort: a failure here must not fail a save that already succeeded —
   * the form is persisted and the connection can be created on the next save.
   */
  const anchorProcessConnections = useCallback(
    async (savedDefinitionId: string, saved: FormSection[]) => {
      if (!selectedTenantId) return
      await Promise.all(
        [...processFieldIds(saved)].map(fieldId =>
          processStepsApi
            .getOrCreate(selectedTenantId, fieldId, savedDefinitionId)
            .catch(() => undefined),
        ),
      )
    },
    [selectedTenantId],
  )

  /**
   * Tell the rest of the app a save changed the process world.
   *
   * Saving here creates Field Library rows and process-step connections through
   * the API clients DIRECTLY, not through the mutation hooks that would
   * normally invalidate on success — so nothing informed the caches and the
   * Process page kept serving a list from before the save. It looked like the
   * new process had not been created, and only a full page load corrected it.
   *
   * Invalidated by PREFIX so every dependent key is covered at once: the
   * process list, the forms-that-have-processes map behind the Process page's
   * pickers, and each field's own connection. A form save can change all three
   * — adding a Process field creates a connection AND makes its form eligible
   * for the picker.
   */
  const refreshProcessCaches = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['process-steps', selectedTenantId] })
    // Registration mints Field Library rows for canvas-added process fields.
    void queryClient.invalidateQueries({ queryKey: ['fieldLibrary'] })
  }, [queryClient, selectedTenantId])

  /**
   * Delete the processes a save just orphaned.
   *
   * Three gates, in order, and anything that fails one is left alone:
   *  - still used by another form (or an earlier version of this one) — the
   *    usage check runs AFTER the save, so this form no longer counts itself;
   *  - the process has been run, which the server refuses with a 409; only then
   *  - the Field Library row goes too, since nothing references it any more.
   *
   * Reports what it could not remove rather than failing the save, which has
   * already succeeded by this point.
   */
  const deleteOrphanedProcesses = useCallback(
    async (fieldIds: string[]) => {
      if (!selectedTenantId || fieldIds.length === 0) return
      const blocked: string[] = []
      for (const fieldId of fieldIds) {
        try {
          const usage = await fieldLibraryApi.getUsage(fieldId).catch(() => null)
          if (usage && usage.count > 0) continue
          await processStepsApi.remove(selectedTenantId, fieldId).catch((e: unknown) => {
            // 404 just means the field never had a configured process.
            if (e instanceof Error && !/\(404\)/.test(e.message)) throw e
          })
          await fieldLibraryApi.deleteField(fieldId).catch(() => undefined)
        } catch (e) {
          blocked.push(e instanceof Error ? e.message : String(e))
        }
      }
      if (blocked.length > 0) {
        setLoadError(
          `The form was saved. Some processes could not be removed: ${blocked.join(' ')}`,
        )
      }
    },
    [selectedTenantId],
  )

  const registerCanvasProcessFields = useCallback(async (): Promise<FormSection[]> => {
    const pending = unregisteredProcessFields(sections)
    if (pending.length === 0) return sections

    /*
     * Refuse to file a process under a name that identifies nothing
     * (z8nrz7cr9r).
     *
     * A palette-dropped process starts out labelled with the field TYPE, and
     * this handler files it in the Field Library under its label — so leaving the
     * default in place across three forms produced three library rows called
     * "Process", which is precisely the data this environment ended up with.
     * Since config is keyed on the library field, picking the wrong one silently
     * edits another form's bill of materials.
     *
     * Thrown before any API call, so a rejected save creates no partial state.
     * All three save paths funnel through here inside a catch that surfaces the
     * message, so this one guard covers every one of them.
     */
    /*
     * Two different processes cannot share a name (z8nrz7cr9r).
     *
     * The API refuses it, and these creates run in a Promise.all — so a
     * rejection arriving after a sibling has already been created would leave an
     * orphan library row. Checked here, before any request, so the save either
     * happens whole or not at all. Compared by resolved id, since a form
     * embedding one process twice is legitimate and shares its configuration.
     */
    const conflicts = conflictingProcessNames(sections)
    if (conflicts.length > 0) {
      throw new Error(
        `Two different processes on this form share the name ${conflicts
          .map(l => `“${l}”`)
          .join(', ')}. Process configuration is shared by name, so rename one — ` +
          `or insert the same process from the Field Library if they are meant to ` +
          `be the same step.`,
      )
    }

    const unnamed = unnamedProcessFields(sections, FIELD_LABELS.process)
    if (unnamed.length > 0) {
      const count = unnamed.length
      throw new Error(
        `${count} Process field${count === 1 ? '' : 's'} still ${
          count === 1 ? 'has' : 'have'
        } the default name “${FIELD_LABELS.process}”. Rename ${
          count === 1 ? 'it' : 'them'
        } on the canvas before saving — a process is filed in the Field Library ` +
          `under its label, and several called “${FIELD_LABELS.process}” cannot be ` +
          `told apart afterwards.`,
      )
    }

    const created = await Promise.all(
      pending.map(f =>
        fieldLibraryApi
          .createField({
            // No `|| FIELD_LABELS.process` fallback any more: a blank label is
            // refused above rather than quietly becoming the ambiguous default.
            label: f.label.trim(),
            fieldType: 'process',
            required: f.required,
          })
          .then(lib => [f.id, lib.id] as const),
      ),
    )
    const next = stampLibraryFieldIds(sections, new Map(created))
    setSections(next)
    return next
  }, [sections])

  // On save: new forms create directly; existing forms save in place. A form
  // version that patients use takes only minor edits in place (layout, colours,
  // help text, alert groups and the like); the server answers a structural
  // edit with NEW_VERSION_REQUIRED, which opens the signed "Save as New
  // Version" dialog instead.
  const runSave = useCallback(
    async (removedToDelete: string[]) => {
      if (!definitionId) {
        setSaving(true)
        setLoadError(null)
        try {
          const registered = await registerCanvasProcessFields()
          const created = await formsApi.saveDefinition(buildPayload(registered))
          await anchorProcessConnections(created.id, registered)
          refreshProcessCaches()
          savedProcessIds.current = processFieldIds(registered)
          navigate(`/form-builder/${created.id}`, { replace: true })
          setJustSaved(true)
          setTimeout(() => setJustSaved(false), 1500)
        } catch (e) {
          setLoadError(e instanceof Error ? e.message : 'Save failed.')
        } finally {
          setSaving(false)
        }
        return
      }
      setSaving(true)
      setLoadError(null)
      const askForNewVersion = () => {
        setVersionError(null)
        setNewVersionInput(versionLabel((defVersion ?? 1) + 1))
        setVersionDialogOpen(true)
      }
      try {
        // A new Process field is a structural edit. On a version in use, ask for
        // the new version before filing it in the Field Library, so cancelling
        // the dialog leaves no library rows behind. A failed check falls through
        // to the save, where the server still decides.
        if (unregisteredProcessFields(sections).length > 0) {
          const inUseNow = await formsApi.checkInUse(definitionId).then(r => r.inUse, () => false)
          if (inUseNow) {
            askForNewVersion()
            return
          }
        }
        const registered = await registerCanvasProcessFields()
        try {
          await formsApi.updateDefinition(definitionId, buildPayload(registered))
        } catch (e) {
          if (!isNewVersionRequired(e)) throw e
          askForNewVersion()
          return
        }
        await anchorProcessConnections(definitionId, registered)
        // After the update, so this form's own definition no longer counts
        // itself as a user of the process being removed.
        await deleteOrphanedProcesses(removedToDelete)
        refreshProcessCaches()
        savedProcessIds.current = processFieldIds(registered)
        // Snapshot what was actually SAVED, not what was on screen when this
        // callback was created. Registration stamps `libraryFieldId` onto the
        // sections, so the closed-over `currentJson` describes the pre-stamp
        // form — storing it leaves the form reading as dirty the instant it
        // saves. Shape must stay in step with the `currentJson` memo.
        setSavedJson(
          JSON.stringify({
            name: formMeta.name.trim(),
            category: defMeta.category,
            allowedActions: defMeta.allowedActions,
            defaultRepeatCount: defMeta.defaultRepeatCount ?? null,
            completionGate: defMeta.completionGate,
            completerGroupIds: defMeta.completerGroupIds ?? [],
            fieldLayout: defMeta.fieldLayout ?? null,
            sections: registered,
          }),
        )
        setJustSaved(true)
        setTimeout(() => setJustSaved(false), 1500)
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : 'Save failed.')
      } finally {
        setSaving(false)
      }
    },
    [
      definitionId,
      defVersion,
      sections,
      buildPayload,
      formMeta.name,
      defMeta.category,
      defMeta.allowedActions,
      defMeta.completionGate,
      defMeta.completerGroupIds,
      defMeta.fieldLayout,
      navigate,
      registerCanvasProcessFields,
      anchorProcessConnections,
      deleteOrphanedProcesses,
    ],
  )

  const handleSaveForm = useCallback(async () => {
    if (!formMeta.name.trim()) {
      setLoadError('Form name is required.')
      return
    }
    // Removing a Process field orphans the process configured for it. Ask
    // rather than assume: the config may represent real setup work, and the
    // author may be mid-rearrangement rather than deleting for good.
    const removed = removedProcessFieldIds(savedProcessIds.current, sections)
    if (removed.length > 0) {
      setPendingRemovals(removed)
      return
    }
    await runSave([])
  }, [formMeta.name, sections, runSave])

  // The label the current version is known by in the catalog — what the new
  // one has to differ from. Mirrors FormDefinitionsPage's Version column,
  // which renders `versionName ?? V{version}`.
  const currentVersionLabel = defVersionName ?? versionLabel(defVersion)

  // AC B1: auto-proposed, user-editable, but "required to differ from the
  // previous". The integer `version` is the ordering key and auto-increments
  // server-side; this input is the user-facing label (`versionName`), so the
  // differs-check belongs here, on the value the admin actually edits.
  const newVersionValid =
    newVersionInput.trim().length > 0 &&
    newVersionInput.trim().toLowerCase() !== currentVersionLabel.trim().toLowerCase()

  const confirmSaveVersion = useCallback(
    // The dialog hands back a signing token, not a name: WHO signed is the
    // authenticated account, and only the password proves it is them.
    async (signingToken: string) => {
      if (!definitionId) return
      setSaving(true)
      setVersionError(null)
      try {
        const registered = await registerCanvasProcessFields()
        const created = await formsApi.createDefinitionVersion(definitionId, {
          ...buildPayload(registered),
          versionName: newVersionInput.trim(),
          signingToken,
        })
        await anchorProcessConnections(created.id, registered)
        refreshProcessCaches()
        // No orphan cleanup on this path, deliberately: versioning keeps the
        // previous definition, which still embeds any Process field dropped
        // here. The process is therefore still in use, and deleting it would
        // strand the older version.
        savedProcessIds.current = processFieldIds(registered)
        // The new version is born detached — createVersion never copies
        // crossFormTemplateId, so it is NULL from the moment this row exists.
        // Set synchronously, before navigate: this component does not remount
        // on a version save (same route, new id via `replace`), so without
        // this there is a one-tick window where useCrossFormAuthoring still
        // reads the OLD version's cached defMeta and reseeds the picker to the
        // template that no longer applies.
        setDefMeta(prev => ({ ...prev, crossFormTemplateId: null }))
        setVersionDialogOpen(false)
        navigate(`/form-builder/${created.id}`, { replace: true })
        setJustSaved(true)
        setTimeout(() => setJustSaved(false), 1500)
      } catch (e) {
        setVersionError(e instanceof Error ? e.message : 'Save failed.')
      } finally {
        setSaving(false)
      }
    },
    [definitionId, newVersionInput, buildPayload, navigate, registerCanvasProcessFields],
  )

  useEffect(() => {
    if (!definitionId) return
    formsApi
      .getDefinition(definitionId)
      .then(def => {
        setFormMeta({ name: def.name, description: '' })
        setDefVersion(def.version ?? null)
        setDefVersionName(def.versionName ?? null)
        setDefActive(typeof def.active === 'boolean' ? def.active : null)
        setDefIdentity({ familyId: def.familyId, handle: def.handle })
        const loadedCategory = def.category
        const loadedAllowedActions = def.allowedActions ?? []
        // The row stores null for "no gate"; the editor's switch reads undefined.
        const loadedCompletionGate = def.completionGate ?? undefined
        const loadedCompleterGroupIds = def.completerGroupIds ?? []
        const loadedFieldLayout = sanitizeFieldLayout(def.fieldLayout)
        setDefMeta({
          category: loadedCategory,
          allowedActions: loadedAllowedActions,
          defaultRepeatCount: def.defaultRepeatCount ?? null,
          completionGate: loadedCompletionGate,
          completerGroupIds: loadedCompleterGroupIds,
          // Restores the cross-form template context chosen last time; without
          // it the picker silently fell back to its default (None, for a form
          // in several templates) and no cross-form ref resolved.
          crossFormTemplateId: def.crossFormTemplateId ?? null,
          fieldLayout: loadedFieldLayout,
        })
        // getSections migrates legacy flat/columns shapes to sections on the fly.
        const loadedSections = getSections(def).map(sec => ({
          id: sec.id,
          title: sec.title,
          width: sec.width,
          gatedByPrevious: sec.gatedByPrevious,
          unlockedBySections: sec.unlockedBySections,
          visibility: sec.visibility,
          headerColor: sec.headerColor,
          fields: sec.fields.map(fieldDefToFormField),
        }))
        setSections(loadedSections)
        // The processes this form arrives with — the baseline a later save
        // compares against to detect what a removal would orphan.
        savedProcessIds.current = processFieldIds(loadedSections)
        // Capture the persisted state so the Save button can be disabled when nothing changed.
        setSavedJson(JSON.stringify({ name: def.name, category: loadedCategory, allowedActions: loadedAllowedActions, defaultRepeatCount: def.defaultRepeatCount ?? null, completionGate: loadedCompletionGate, completerGroupIds: loadedCompleterGroupIds, fieldLayout: loadedFieldLayout, sections: loadedSections }))
        setStep('builder') // existing forms skip the properties step
      })
      .catch(e => {
        setLoadError(e instanceof Error ? e.message : 'Failed to load definition.')
        setStep('builder')
      })
  }, [definitionId, reloadKey])

  useEffect(() => {
    if (!definitionId) { setInUse(null); return }
    formsApi.checkInUse(definitionId).then(r => setInUse(r.inUse)).catch(() => setInUse(null))
  }, [definitionId])

  // Cross-form reference authoring: which procedure templates contain this
  // form and which forms come before it. New (unsaved) forms are in no
  // template, so the cross-form pickers stay hidden for them.
  const crossFormAuthoring = useCrossFormAuthoring(
    definitionId
      ? {
          formId: definitionId,
          familyId: defIdentity.familyId,
          crossFormTemplateId: defMeta.crossFormTemplateId,
        }
      : null,
  )

  // Cross-form refs stored anywhere on this form. A new version starts detached
  // from the template context, so these are exactly the references that stop
  // resolving until the author rewires them.
  const crossFormRefCount = useMemo(() => {
    const refs = new Set<string>()
    const addFrom = (tokens: string[]) => {
      for (const t of tokens) if (isCrossFormRef(t)) refs.add(t)
    }
    for (const section of sections) {
      addFrom(visibilitySourceIds(section.visibility))
      for (const field of section.fields) {
        if (field.formula) addFrom(extractFormulaRefs(field.formula))
        for (const formula of calculatedCellFormulas(field.tableConfig)) addFrom(extractFormulaRefs(formula))
        addFrom(visibilitySourceIds(field.visibility))
        for (const rule of notificationRules(field)) addFrom(notificationSourceIds(rule))
      }
    }
    return refs.size
  }, [sections])

  function handleCategoryChange(category: FormCategory) {
    setDefMeta(prev => ({ ...prev, category }))
    // Switching to 'anchored' anchors every existing eligible field on the spot —
    // the same rule anchorForCategory applies to fields added afterwards.
    if (category === 'anchored') {
      setSections(prev => anchorAllEligibleFields(prev))
    }
  }

  // The count arrives with the actions when `Repeat` is toggled, so the pair
  // moves in ONE state update and can never be half-applied.
  function handleAllowedActionsChange(
    allowedActions: string[],
    defaultRepeatCount?: number | null,
  ) {
    setDefMeta(prev => ({
      ...prev,
      allowedActions,
      ...(defaultRepeatCount !== undefined ? { defaultRepeatCount } : {}),
    }))
  }

  function handleDefaultRepeatCountChange(defaultRepeatCount: number | null) {
    setDefMeta(prev => ({ ...prev, defaultRepeatCount }))
  }

  function handleCompleterGroupIdsChange(completerGroupIds: string[]) {
    setDefMeta(prev => ({ ...prev, completerGroupIds }))
  }

  function handleCompletionGateChange(completionGate: FormulaGate | undefined) {
    setDefMeta(prev => ({ ...prev, completionGate }))
  }

  function handleContinueToBuilder() {
    if (!formMeta.name.trim()) {
      setPropertiesError('Form name is required.')
      return
    }
    if (newFormHandle && !FORM_HANDLE_RE.test(newFormHandle)) {
      setPropertiesError(
        'Form handle must be lowercase letters/numbers separated by single hyphens (e.g. patient-chart-x7k).',
      )
      return
    }
    setPropertiesError(null)
    setStep('builder')
  }

  function handleEditProperties() {
    setPreviewOpen(false)
    setSelectedId(null)
    setStep('properties')
  }

  // ─── Excel template download / spreadsheet import ──────────────────────────
  const [excelBusy, setExcelBusy] = useState(false)
  const [excelErrors, setExcelErrors] = useState<string[]>([])
  const [excelMissingFields, setExcelMissingFields] = useState<string[]>([])
  // Operands the last import could not resolve. Non-blocking: the form loaded.
  const [excelUnresolvedRefs, setExcelUnresolvedRefs] = useState<string[]>([])

  const handleDownloadTemplate = useCallback(async () => {
    setExcelBusy(true)
    setExcelErrors([])
    setExcelMissingFields([])
    try {
      await downloadFormTemplate()
    } catch (e) {
      setExcelErrors([e instanceof Error ? e.message : 'Failed to build the template.'])
    } finally {
      setExcelBusy(false)
    }
  }, [])

  const handleImportExcel = useCallback(async (file: File) => {
    setExcelBusy(true)
    setExcelErrors([])
    setExcelMissingFields([])
    setExcelUnresolvedRefs([])
    try {
      const fields = await fieldLibraryApi.listAllFields()
      const data = await file.arrayBuffer()
      const res = await parseFormWorkbook(data, fields)
      if (!res.ok || !res.form) {
        setExcelErrors(res.errors)
        setExcelMissingFields(res.missingFieldNames)
        return
      }
      // Carried into the builder step, where the notice above the canvas names
      // them — the import panel this runs from unmounts on success.
      setExcelUnresolvedRefs(res.unresolvedRefs)
      // Hydrate the builder with an unsaved draft — the user reviews, then saves.
      setFormMeta({ name: res.form.meta.name, description: res.form.meta.description })
      setDefMeta(prev => ({ ...prev, category: res.form!.category, fieldLayout: res.form!.fieldLayout ?? null }))
      setSections(
        res.form.category === 'anchored'
          ? anchorAllEligibleFields(res.form.sections)
          : res.form.sections,
      )
      setPropertiesError(null)
      setStep('builder')
    } catch (e) {
      setExcelErrors([e instanceof Error ? e.message : 'Failed to import the spreadsheet.'])
    } finally {
      setExcelBusy(false)
    }
  }, [])

  const [libraryOpen, setLibraryOpen] = useState(false)
  // Every field on the form as it is now, for the formula pickers' suggestions.
  const flatFields = useMemo(() => sections.flatMap(sec => sec.fields), [sections])

  // Forms in the 'anchored' category surface every field in the procedure header, so
  // fields added to them start with "Anchor to procedure header" switched on. Only
  // types that expose the toggle get it (sections/tables/calculated don't).
  const anchorForCategory = useCallback(
    (f: FormField): FormField =>
      defMeta.category === 'anchored' && anchorEligible(f) ? { ...f, anchored: true } : f,
    [defMeta.category],
  )

  // Every add below selects what it adds from inside the setSections updater,
  // and mints it through `minted ??=`. Ids carry a random suffix and React may
  // run an updater twice (StrictMode does, in dev), so minting per call would
  // select one field while keeping another — the panel then shows Form
  // Settings instead of the new field. Minting once keeps the two in step.
  const addSection = useCallback(() => {
    let minted: FormSection | undefined
    setSections(prev => {
      const ns = (minted ??= createSection(`Section ${prev.length + 1}`))
      setSelectedId(ns.id)
      return [...prev, ns]
    })
  }, [])

  // Palette click: 'Section' adds a container; any other type appends a field to
  // the active section (selected section, the selected field's section, or last).
  const addField = useCallback(
    (type: FieldType) => {
      if (type === 'section') {
        addSection()
        return
      }
      let minted: FormField | undefined
      setSections(prev => {
        const base = prev.length ? prev : [createSection('Section 1')]
        const activeId =
          base.find(s => s.id === selectedId)?.id ??
          base.find(s => s.fields.some(f => f.id === selectedId))?.id ??
          base[base.length - 1].id
        const nf = (minted ??= anchorForCategory(createField(type, allFieldIds(base))))
        setSelectedId(nf.id)
        return base.map(s => (s.id === activeId ? { ...s, fields: [...s.fields, nf] } : s))
      })
    },
    [selectedId, addSection, anchorForCategory],
  )

  // The section a palette or library insert lands in: the selected section, the
  // selected field's section, or the last one (null only when there are none).
  const activeSectionId =
    sections.find(s => s.id === selectedId)?.id ??
    sections.find(s => s.fields.some(f => f.id === selectedId))?.id ??
    sections[sections.length - 1]?.id ??
    null

  // Insert library fields (copy-by-value, each stamped with its library
  // provenance) at the end of the section the picker targets, in order.
  // Calculated fields arrive with a re-mapped formula via opts so they stay
  // valid on this form. Returns why nothing was inserted, or null. The picker
  // stays open: closing it is the author's call (z8nrz7dewa).
  const insertLibraryFields = useCallback(
    (defs: FieldDefinition[], sectionId: string | null, opts?: { formula?: string }): string | null => {
      if (defs.length === 0) return null
      if (!sectionId || !sectionsRef.current.some(s => s.id === sectionId)) return 'Select a target section first.'
      let minted: FormField[] | undefined
      setSections(prev => {
        // One shared set: each mint records its id so same-label members can't collide.
        const usedIds = allFieldIds(prev)
        const copies = (minted ??= defs.map(def =>
          anchorForCategory(createFieldFromLibrary(def, { ...opts, usedIds })),
        ))
        setSelectedId(copies[copies.length - 1].id)
        return prev.map(s => (s.id === sectionId ? { ...s, fields: [...s.fields, ...copies] } : s))
      })
      return null
    },
    [anchorForCategory],
  )

  useEffect(() => {
    sectionsRef.current = sections
  }, [sections])

  // Snapshot the layout a field drag starts from, so `handleDragEnd` can rewind
  // it if the drag is canceled.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDragStart = useCallback((event: any) => {
    const data = event.operation?.source?.data
    dragSnapshotRef.current = data?.origin === 'field' ? sectionsRef.current : null
    if (data?.origin === 'option' && data.fieldId) {
      const field = sectionsRef.current
        .flatMap(s => s.fields)
        .find(f => f.id === data.fieldId)
      optionDragSnapshotRef.current = field
        ? { fieldId: field.id, options: field.options }
        : null
    } else {
      optionDragSnapshotRef.current = null
    }
  }, [])

  /**
   * Apply cross-section (and within-section) field moves *while dragging*.
   *
   * This is load-bearing for more than live feedback. `useSortable`'s default
   * OptimisticSortingPlugin re-parents the dragged DOM node itself on dragover
   * (`insertAdjacentElement`) whenever source and target are both sortables. For
   * a move between two sections that silently hands the node to the destination
   * container behind React's back, and the next render crashes with
   * "NotFoundError: Failed to execute 'removeChild'" — React asks the ORIGIN
   * section to drop a child that no longer lives there. Within one section it's
   * harmless, because that's a sibling reorder React reconciles with
   * insertBefore. See https://app.clickup.com/t/86eyfxwky.
   *
   * Updating state here removes the hazard at the source: the plugin re-checks
   * every sortable's group/index after React has rendered and bails out when
   * they no longer match what it captured, so it never touches the DOM at all.
   *
   * Deliberately does NOT prune unlock/visibility refs — a field dragged ACROSS
   * a section on its way somewhere else would have its rules stripped in
   * passing, and nothing restores them. Pruning happens once, at drop.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDragOver = useCallback((event: any) => {
    const data = event.operation?.source?.data
    // Reordering a select field's inline options (z8nrz7djxb). A flat,
    // single-container list, so the cross-container crash above cannot happen
    // here — but the move is still applied to state during dragover so that
    // application state stays the source of truth and the plugin leaves the DOM
    // alone, exactly as for fields.
    if (data?.origin === 'option' && data.fieldId) {
      flushSync(() => {
        setSections(prev => applyOptionMoveInSections(prev, data.fieldId, event))
      })
      return
    }
    if (data?.origin !== 'field') return
    flushSync(() => {
      setSections(prev => applyFieldMove(prev, event))
    })
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDragEnd = useCallback((event: any) => {
    const source = event.operation?.source
    const target = event.operation?.target
    if (!source) return
    const data = source.data as
      | { origin?: string; fieldType?: FieldType; libraryField?: FieldDefinition }
      | undefined
    if (!data) return

    const targetId = target ? String(target.id) : null

    // ── Reorder a select field's inline options ───────────────────────────────
    // Applied live during dragover like a field move, so anything that isn't a
    // committed reorder has to restore the pre-drag order rather than leave the
    // preview standing. Never falls through to the branches below: an option
    // drag must not touch the canvas.
    //
    // Two distinct routes, and `event.canceled` only covers one of them:
    //  - AC1.5 Escape → dnd-kit's handleCancel stops with `canceled: true`.
    //  - AC1.4 dropped outside the list → an ordinary pointerup, where
    //    handlePointerUp computes `canceled = !status.initialized` and so
    //    reports FALSE for any real drag. Testing `canceled` alone would leave
    //    the previewed reorder committed and the form dirty — and since a saved
    //    definition is order-only, that is a silent data change, not a cosmetic
    //    one. So a drop whose target isn't a row of this list restores too.
    if (data.origin === 'option') {
      const snapshot = optionDragSnapshotRef.current
      optionDragSnapshotRef.current = null
      if (!snapshot) return
      const committed = !event.canceled && isOptionRowTarget(targetId, snapshot.options)
      if (!committed) {
        setSections(prev => restoreFieldOptions(prev, snapshot.fieldId, snapshot.options))
      }
      return
    }

    // ── Move an existing field within / across sections ───────────────────────
    // Handled before the canceled-drag bail-out below: unlike every other
    // origin, this one has already mutated `sections` during dragover, so a
    // cancel means restore rather than do-nothing.
    if (data.origin === 'field') {
      const snapshot = dragSnapshotRef.current
      dragSnapshotRef.current = null
      if (event.canceled) {
        if (snapshot) setSections(snapshot)
        return
      }
      setSections(prev => {
        // dragover normally placed the field already. It bails on targets
        // dnd-kit's `move` can't map (a section header) — finish those here.
        const alreadyPlaced = snapshot ? !sameFieldLayout(prev, snapshot) : true
        const next = alreadyPlaced
          ? prev
          : placeFieldByTarget(prev, String(source.id), targetId)
        // Also drops section rules whose source field was just dragged inside
        // the watching section — the backend rejects that shape on save.
        return pruneVisibilityRefs(pruneUnlockRefs(next))
      })
      return
    }

    if (event.canceled) return

    // ── Reorder a section relative to the others ──────────────────────────────
    if (data.origin === 'section') {
      const sectionId = String(source.id).replace(/^section:/, '')
      // Read now: the slot the drag slid the section to (see reorderSections).
      const drop = isSortable(source)
        ? { index: source.index, initialIndex: source.initialIndex, targetId }
        : { targetId }
      setSections(prev => {
        const reordered = reorderSections(prev, sectionId, drop)
        if (reordered === prev) return prev
        // Reordering sections shifts both section order and the flat field order —
        // prune forward/dangling refs at both levels.
        return pruneSectionUnlockRefs(pruneUnlockRefs(reordered))
      })
      return
    }

    // ── Drop from the palette ────────────────────────────────────────────────
    if (data.origin === 'palette' && data.fieldType) {
      const fieldType = data.fieldType
      // The Section palette item creates a new container (near the drop target).
      if (fieldType === 'section') {
        let minted: FormSection | undefined
        setSections(prev => {
          const ns = (minted ??= createSection(`Section ${prev.length + 1}`))
          setSelectedId(ns.id)
          const destId = resolveSectionId(prev, targetId)
          if (!destId) return [...prev, ns]
          const idx = prev.findIndex(s => s.id === destId)
          return [...prev.slice(0, idx + 1), ns, ...prev.slice(idx + 1)]
        })
        return
      }
      let minted: FormField | undefined
      setSections(prev => {
        if (prev.length === 0) {
          const sec = createSection('Section 1')
          const nf = (minted ??= anchorForCategory(createField(fieldType)))
          sec.fields = [nf]
          setSelectedId(nf.id)
          return [sec]
        }
        const destId = resolveSectionId(prev, targetId) ?? prev[prev.length - 1].id
        const nf = (minted ??= anchorForCategory(createField(fieldType, allFieldIds(prev))))
        setSelectedId(nf.id)
        return prev.map(s => {
          if (s.id !== destId) return s
          if (isFieldTarget(targetId) && s.fields.some(f => f.id === targetId)) {
            const idx = s.fields.findIndex(f => f.id === targetId)
            return { ...s, fields: [...s.fields.slice(0, idx), nf, ...s.fields.slice(idx)] }
          }
          return { ...s, fields: [...s.fields, nf] }
        })
      })
      return
    }

    // ── Drop a saved field from the library picker ────────────────────────────
    if (data.origin === 'library' && data.libraryField) {
      const libraryField = data.libraryField
      let minted: FormField | undefined
      setSections(prev => {
        if (prev.length === 0) {
          const sec = createSection('Section 1')
          const nf = (minted ??= anchorForCategory(createFieldFromLibrary(libraryField)))
          sec.fields = [nf]
          setSelectedId(nf.id)
          return [sec]
        }
        const destId = resolveSectionId(prev, targetId) ?? prev[prev.length - 1].id
        const nf = (minted ??= anchorForCategory(
          createFieldFromLibrary(libraryField, { usedIds: allFieldIds(prev) }),
        ))
        setSelectedId(nf.id)
        return prev.map(s => {
          if (s.id !== destId) return s
          if (isFieldTarget(targetId) && s.fields.some(f => f.id === targetId)) {
            const idx = s.fields.findIndex(f => f.id === targetId)
            return { ...s, fields: [...s.fields.slice(0, idx), nf, ...s.fields.slice(idx)] }
          }
          return { ...s, fields: [...s.fields, nf] }
        })
      })
      return
    }
  }, [anchorForCategory])

  const updateField = useCallback((updated: FormField) => {
    setSections(prev =>
      prev.map(s => ({ ...s, fields: s.fields.map(f => (f.id === updated.id ? updated : f)) })),
    )
  }, [])

  const deleteField = useCallback((id: string) => {
    setSections(prev =>
      // Also drops visibility conditions that watched the deleted field.
      pruneVisibilityRefs(
        prev.map(s => ({
          ...s,
          fields: s.fields
            .filter(f => f.id !== id)
            // Drop the deleted field from any prerequisite list — no dangling refs.
            .map(f => {
              const ids = prerequisiteIds(f)
              if (!ids.includes(id)) return f
              const kept = ids.filter(pid => pid !== id)
              return { ...f, unlockedBy: kept.length ? kept : undefined }
            }),
        })),
      ),
    )
    setSelectedId(prev => (prev === id ? null : prev))
  }, [])

  // Delete-field entry point wired to the canvas (z8nrz7d0h6, Part 2). A field
  // with no dependents deletes immediately — unchanged from before this
  // ticket (AC2.5). A field with dependents opens a confirmation naming them
  // first; deletion is never blocked, only warned (AC2.3).
  const requestDeleteField = useCallback((id: string) => {
    const dependents = findFieldDependents(sections, id)
    if (dependents.length === 0) {
      deleteField(id)
      return
    }
    const target = sections.flatMap(s => s.fields).find(f => f.id === id)
    setPendingFieldDeletion({ id, label: target?.label || 'This field', dependents })
  }, [sections, deleteField])

  // Set a field's sequencing prerequisites from the canvas inline picker.
  // An empty selection clears the lock (always unlocked). Fine-grained selection
  // also lives in the Properties panel — both edit the same `unlockedBy`.
  const setFieldUnlockedBy = useCallback((fieldId: string, ids: string[]) => {
    setSections(prev =>
      prev.map(s => ({
        ...s,
        fields: s.fields.map(f =>
          f.id === fieldId ? { ...f, unlockedBy: ids.length ? ids : undefined } : f,
        ),
      })),
    )
  }, [])

  const updateSection = useCallback((updated: FormSection) => {
    setSections(prev => prev.map(s => (s.id === updated.id ? updated : s)))
  }, [])

  const deleteSection = useCallback((id: string) => {
    // Drop the section, then prune BOTH prerequisite levels: section refs to the
    // removed section, and field refs to the fields it took with it — no dangling
    // or locked-forever prerequisites.
    setSections(prev =>
      pruneVisibilityRefs(pruneUnlockRefs(pruneSectionUnlockRefs(prev.filter(s => s.id !== id)))),
    )
    setSelectedId(prev => (prev === id ? null : prev))
  }, [])

  /**
   * Download the open form as a JSON file `ImportFormDialog` can read back
   * (z8nrz7ep1z).
   *
   * Writes SECTION DEFINITIONS, not the builder's `FormSection` state: those
   * are the same shape the backend persists and the copy builder consumes, so
   * the file stays readable as builder internals drift. The builder's
   * `description` is deliberately not in the file — it is never persisted
   * (`FormDefinition` has no such property and the editor resets it to `''` on
   * load), so the old export was writing a value nothing could restore.
   */
  const exportJson = async () => {
    const body = {
      name: formMeta.name.trim(),
      category: defMeta.category,
      allowedActions: defMeta.allowedActions,
      defaultRepeatCount: defMeta.defaultRepeatCount ?? null,
      completionGate: defMeta.completionGate,
      completerGroupIds: defMeta.completerGroupIds ?? [],
      fieldLayout: defMeta.fieldLayout ?? null,
      sections: toSectionDefinitions(sections),
    }
    // The option sets the dropdowns are bound to travel by value (z8nrz7f3qt):
    // their ids mean nothing in another tenant. Refused rather than exported
    // without them, because such a file would fail to import anywhere else.
    const setIds = referencedOptionSetIds(body)
    let optionSets: FormExportOptionSet[] = []
    if (setIds.length > 0) {
      try {
        const { items } = await optionSetsApi.list({ ids: setIds.join(',') })
        optionSets = items.map(set => ({
          id: set.id,
          name: set.name,
          values: set.options.map(o => o.label),
        }))
      } catch (e) {
        setLoadError(
          `Export failed: could not read the option sets this form uses. ${e instanceof Error ? e.message : ''}`.trim(),
        )
        return
      }
    }
    const file = buildFormExportFile(body, new Date(), optionSets)
    const blob = new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = formExportFileName(formMeta.name)
    a.click()
    URL.revokeObjectURL(url)
  }

  const allFieldsFlat = useMemo(() => sections.flatMap(s => s.fields), [sections])
  const selectedSection = sections.find(s => s.id === selectedId) ?? null
  const selectedField = selectedSection
    ? null
    : allFieldsFlat.find(f => f.id === selectedId) ?? null
  const hasSelection = !!(selectedSection || selectedField)
  const totalFields = allFieldsFlat.length
  const showFormSettings = !previewOpen && !selectedId

  // ── Visibility click-to-pick ────────────────────────────────────────────────
  // While a condition row in the Properties panel is armed, the next canvas
  // click on an eligible field becomes that condition's source instead of a
  // selection. Cleared whenever the selection changes.
  const [visibilityPick, setVisibilityPick] = useState<VisibilityPickTarget | null>(null)
  useEffect(() => setVisibilityPick(null), [selectedId])

  // Fields pickable as a source for the armed condition — value-bearing, not
  // the target itself, no visibility cycles; for a section target, not inside it.
  const pickEligibleIds = useMemo(() => {
    if (!visibilityPick) return null
    if (selectedField) {
      return new Set(
        allFieldsFlat
          .filter(
            f =>
              f.id !== selectedField.id &&
              f.type !== 'table' &&
              f.type !== 'section' &&
              !wouldCreateVisibilityCycle(sections, selectedField.id, f.id),
          )
          .map(f => f.id),
      )
    }
    if (selectedSection) {
      return new Set(
        allFieldsFlat
          .filter(
            f =>
              f.type !== 'table' &&
              f.type !== 'section' &&
              !selectedSection.fields.some(sf => sf.id === f.id) &&
              !wouldCreateVisibilityCycle(sections, selectedSection.id, f.id),
          )
          .map(f => f.id),
      )
    }
    return null
  }, [visibilityPick, selectedField, selectedSection, allFieldsFlat, sections])

  // Route a canvas pick into the armed condition row of the selected target.
  const handleVisibilityPick = useCallback(
    (sourceFieldId: string) => {
      if (!visibilityPick) return
      const { groupIdx, condIdx } = visibilityPick
      const source = allFieldsFlat.find(f => f.id === sourceFieldId)
      const validOps = operatorsFor(source)
      const patchRule = (rule: VisibilityRule | undefined): VisibilityRule | undefined => {
        if (!rule?.groups[groupIdx]?.conditions[condIdx]) return rule
        return {
          ...rule,
          groups: rule.groups.map((g, gi) =>
            gi !== groupIdx
              ? g
              : {
                  ...g,
                  conditions: g.conditions.map((c, ci) =>
                    ci !== condIdx
                      ? c
                      : {
                          ...c,
                          sourceFieldId,
                          // Reset an operator the new source can't support.
                          operator: validOps.includes(c.operator) ? c.operator : 'equals',
                          value: '',
                        },
                  ),
                },
          ),
        }
      }
      if (selectedField) {
        updateField({ ...selectedField, visibility: patchRule(selectedField.visibility) })
      } else if (selectedSection) {
        updateSection({ ...selectedSection, visibility: patchRule(selectedSection.visibility) })
      }
      setVisibilityPick(null)
    },
    [visibilityPick, selectedField, selectedSection, allFieldsFlat, updateField, updateSection],
  )

  // ── Notification-rule click-to-pick ─────────────────────────────────────────
  // Separate state from visibilityPick — the two builders' pick-arming never
  // interferes with each other. Field-only (no section target), and no
  // visibility-cycle guard: a notification rule doesn't hide/show anything, so
  // it can't create a visibility cycle, and referencing the field's own value
  // is legal here (unlike for visibility).
  const [notificationPick, setNotificationPick] = useState<NotificationPickTarget | null>(null)
  useEffect(() => setNotificationPick(null), [selectedId])

  const notificationPickEligibleIds = useMemo(() => {
    if (!notificationPick || !selectedField) return null
    return new Set(
      allFieldsFlat.filter(f => f.type !== 'table' && f.type !== 'section').map(f => f.id),
    )
  }, [notificationPick, selectedField, allFieldsFlat])

  const handleNotificationPick = useCallback(
    (sourceFieldId: string) => {
      if (!notificationPick || !selectedField) return
      const { ruleIdx, groupIdx, condIdx } = notificationPick
      const source = allFieldsFlat.find(f => f.id === sourceFieldId)
      const validOps = operatorsFor(source)
      const patchRule = (rule: NotificationRule | undefined): NotificationRule | undefined => {
        if (!rule?.groups[groupIdx]?.conditions[condIdx]) return rule
        return {
          ...rule,
          groups: rule.groups.map((g, gi) =>
            gi !== groupIdx
              ? g
              : {
                  ...g,
                  conditions: g.conditions.map((c, ci) =>
                    ci !== condIdx
                      ? c
                      : {
                          ...c,
                          sourceFieldId,
                          operator: validOps.includes(c.operator) ? c.operator : 'equals',
                          value: '',
                        },
                  ),
                },
          ),
        }
      }
      // Only the armed rule is patched — the field's other rules can have a
      // condition at the very same coordinates.
      const rules = notificationRules(selectedField)
      updateField({
        ...selectedField,
        notifications: rules.map((r, i) => (i === ruleIdx ? (patchRule(r) ?? r) : r)),
        notification: undefined,
      })
      setNotificationPick(null)
    },
    [notificationPick, selectedField, allFieldsFlat, updateField],
  )

  // Only one target's click-to-pick can be "listening" for the next canvas
  // click at a time — whichever is currently armed.
  const combinedPickEligibleIds = visibilityPick ? pickEligibleIds : notificationPickEligibleIds
  const handleCombinedPick = useCallback(
    (id: string) => {
      if (visibilityPick) handleVisibilityPick(id)
      else if (notificationPick) handleNotificationPick(id)
    },
    [visibilityPick, notificationPick, handleVisibilityPick, handleNotificationPick],
  )

  // In-memory definition the preview renders — reflects the current builder state.
  const previewDefinition: FormDefinition = useMemo(() => {
    const now = new Date().toISOString()
    return {
      id: 'preview',
      name: formMeta.name || 'Untitled form',
      category: defMeta.category,
      allowedActions: defMeta.allowedActions,
      version: 1,
      fieldLayout: defMeta.fieldLayout ?? null,
      sections: toSectionDefinitions(sections),
      createdBy: 'preview',
      createdAt: now,
      updatedAt: now,
    }
  }, [formMeta.name, defMeta.category, defMeta.allowedActions, defMeta.fieldLayout, sections])

  // Toggle preview; reset the throwaway submission each time it opens.
  const togglePreview = () => {
    setPreviewOpen(open => {
      const next = !open
      if (next) {
        setPreviewSubmission(makePreviewSubmission())
        setSelectedId(null)
      }
      return next
    })
  }

  // ── Form version: which one is open, and a read-only look at another ──────
  // (z8nrz7dmb1). Everything here is display: no write, no dirty-state change.
  const versionIndicator = formVersionIndicatorState({
    definitionId,
    version: defVersion,
    familyId: defIdentity.familyId,
    active: defActive,
  })
  const viewId = searchParams.get('view')
  const viewingVersionId = definitionId && viewId && viewId !== definitionId ? viewId : null

  const openVersion = (id: string) => {
    if (id === definitionId) return
    // Unsaved changes are never dropped without asking (AC2.7).
    if (isDirty) setPendingVersionId(id)
    else setViewedVersion(id)
  }

  if (viewingVersionId) {
    return (
      <ReadOnlyVersionView
        versionId={viewingVersionId}
        indicator={
          <FormVersionIndicator
            state={versionIndicator}
            definitionId={definitionId}
            currentId={definitionId}
            onOpenVersion={openVersion}
          />
        }
        returnLabel={versionIndicator.text}
        onReturn={() => setViewedVersion(null)}
        backTo={backTo}
        tenantTimezone={selectedTenant?.timezone}
      />
    )
  }

  return (
    <div className='flex h-full flex-col overflow-hidden bg-background'>
      {/* Toolbar */}
      <header className='flex h-14 shrink-0 items-center gap-3 overflow-x-auto border-b px-4'>
        <Link
          to={backTo?.to ?? '/form-builder?tab=forms'}
          className='shrink-0 text-sm text-muted-foreground transition-colors hover:text-foreground'
        >
          ← {backTo?.label ?? 'Form Builder'}
        </Link>
        <div className='h-4 w-px shrink-0 bg-border' />

        {step === 'builder' && (
          <>
            <Badge variant='outline' className='shrink-0 text-xs'>
              {FORM_CATEGORY_LABELS[defMeta.category]}
            </Badge>
            <div className='h-4 w-px shrink-0 bg-border' />
          </>
        )}

        <input
          value={formMeta.name}
          onChange={e => setFormMeta(m => ({ ...m, name: e.target.value }))}
          className='min-w-40 flex-1 border-none bg-transparent text-sm font-semibold outline-none'
          placeholder='Form name…'
        />

        {defIdentity.handle && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='shrink-0 cursor-default rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground'>
                  {defIdentity.handle}
                </span>
              </TooltipTrigger>
              <TooltipContent side='bottom'>
                Permanent form handle — other forms reference this form's fields as{' '}
                {'{'}
                {defIdentity.handle}.fieldId{'}'}. Set at creation; renaming the form never
                changes it.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* The FORM's own version (z8nrz7dmb1) — separate from the template
            selector further right, which is about the template it is authored
            against. */}
        {step === 'builder' && (
          <FormVersionIndicator
            state={versionIndicator}
            definitionId={definitionId}
            currentId={definitionId}
            onOpenVersion={openVersion}
          />
        )}

        {definitionId && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='shrink-0 flex items-center gap-1.5 cursor-default'>
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      inUse === true
                        ? 'bg-green-500'
                        : inUse === false
                          ? 'bg-muted-foreground/40'
                          : 'bg-muted-foreground/20 animate-pulse',
                    )}
                  />
                  <span className='text-xs text-muted-foreground'>
                    {inUse === true ? 'In use' : inUse === false ? 'Not in use' : ''}
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent side='bottom'>
                {inUse === true
                  ? 'This form version has patient submissions. Layout, colours, help text and specifications save in place; other changes create a new version.'
                  : inUse === false
                    ? 'No patient submissions yet — saving will update this version in place.'
                    : 'Checking…'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {loadError && <p className='shrink-0 text-xs text-destructive'>{loadError}</p>}

        <div className='flex shrink-0 items-center gap-2'>
          {step === 'builder' && !definitionId && (
            <Button variant='ghost' size='sm' onClick={handleEditProperties}>
              <Settings className='mr-1.5 h-3.5 w-3.5' />
              Edit Properties
            </Button>
          )}

          {step === 'builder' && (
            <>
            {/* Shown for every SAVED form, not only one that sits in a template:
                a form in no template family still reads `Template: None`, with
                None the only option, rather than the control vanishing and
                leaving the author unable to see what context they are in.
                `available` still gates the cross-form REFERENCING UI below. */}
            {!!definitionId && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <SearchableSelect
                          value={crossFormAuthoring.selectedProcedureTemplateId ?? '__none'}
                          onChange={v =>
                            crossFormAuthoring.selectProcedureTemplate(v === '__none' ? null : v)
                          }
                          // ACTIVE versions only (86eybvqgn criterion D), plus
                          // None, which stays selectable for every form.
                          options={[
                            { value: '__none', label: 'Template: None' },
                            ...crossFormAuthoring.selectableProcedureTemplates.map(p => ({
                              value: p.id,
                              label: templateLabel(p),
                            })),
                          ]}
                          // A form pinned to a since-retired version still shows
                          // it here, with its (vN), so the author can see what
                          // it is on — on the trigger only, never in the list.
                          selectedOption={
                            crossFormAuthoring.pinnedProcedureTemplate
                              ? {
                                  value: crossFormAuthoring.pinnedProcedureTemplate.id,
                                  label: templateLabel(crossFormAuthoring.pinnedProcedureTemplate),
                                }
                              : undefined
                          }
                          placeholder='Template: None'
                          buttonClassName='h-8 w-[240px] text-xs flex items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring'
                     
                          renderOption={o => {
                            if (o.value === '__none') {
                              return (
                                <span className='text-muted-foreground truncate' title='Template: None'>
                                  Template: None
                                </span>
                              )
                            }
                            const t = crossFormAuthoring.procedureTemplates.find(p => p.id === o.value)
                            const fullName =
                              t?.version != null ? `${t.name} (v${t.version})` : (t?.name ?? o.label)
                            return (
                              <span className='flex items-center gap-1 min-w-0' title={fullName}>
                                <span className='truncate'>{t?.name ?? o.label}</span>
                                {t?.version != null && (
                                  <span className='shrink-0 text-muted-foreground'>v{t.version}</span>
                                )}
                              </span>
                            )
                          }}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side='bottom' className='max-w-xs'>
                      Cross-form reference context: formulas and show/hide conditions offer
                      fields from the forms EARLIER than this one in the selected procedure
                      template. The choice is a browsing aid only — it is not stored with the
                      reference.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button
              variant={showFormSettings ? 'default' : 'outline'}
              size='sm'
              onClick={() => {
                setPreviewOpen(false)
                setSelectedId(null)
              }}
            >
              <Settings className='mr-2 h-4 w-4' />
              Form Settings
            </Button>
            </>
            
          )}

          {step === 'builder' && (
            <>
             
              <Button
                variant={previewOpen ? 'default' : 'outline'}
                size='sm'
                onClick={togglePreview}
              >
                {previewOpen ? (
                  <Pencil className='mr-2 h-4 w-4' />
                ) : (
                  <Eye className='mr-2 h-4 w-4' />
                )}
                {previewOpen ? 'Edit' : 'Preview'}
              </Button>
              <Button variant='outline' size='sm' onClick={() => void exportJson()}>
                <Download className='mr-2 h-4 w-4' />
                Export
              </Button>
              <Button variant='outline' size='sm' onClick={() => setImportOpen(true)}>
                <Upload className='mr-2 h-4 w-4' />
                Import
              </Button>
             
              <Button size='sm' onClick={handleSaveForm} disabled={saving || justSaved || !isDirty}>
                {justSaved ? (
                  <Check className='mr-2 h-4 w-4' />
                ) : saving ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <Save className='mr-2 h-4 w-4' />
                )}
                {justSaved ? 'Saved' : 'Save form'}
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={() => navigate('/form-builder?tab=forms')}
              >
                <X className='mr-2 h-4 w-4' />
                Close
              </Button>
            </>
          )}
        </div>
      </header>

      {/* Removing a Process field orphans the process configured for it. The
          config can represent real setup work, and an author may be
          rearranging rather than deleting, so the choice is theirs. */}

      {/* Delete-field warning (z8nrz7d0h6, Part 2) — only opens when the field
          being deleted has dependents (AC2.1); deletion itself is never
          blocked (AC2.3). */}
      <Dialog
        open={pendingFieldDeletion !== null}
        onOpenChange={open => !open && setPendingFieldDeletion(null)}
      >
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Delete {pendingFieldDeletion?.label}?</DialogTitle>
            <DialogDescription>
              Deleting this field leaves the following with a broken reference that must be
              repaired by hand:
            </DialogDescription>
          </DialogHeader>
          <ul className='max-h-40 list-disc space-y-1 overflow-y-auto pl-5 text-sm'>
            {pendingFieldDeletion?.dependents.map(d => (
              <li key={`${d.surface}-${d.id}`}>
                {d.label}{' '}
                <span className='text-muted-foreground'>— {DEPENDENT_SURFACE_LABELS[d.surface]}</span>
              </li>
            ))}
          </ul>
          <DialogFooter className='gap-2'>
            <Button variant='outline' onClick={() => setPendingFieldDeletion(null)}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={() => {
                if (pendingFieldDeletion) deleteField(pendingFieldDeletion.id)
                setPendingFieldDeletion(null)
              }}
            >
              Delete field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingRemovals !== null}
        onOpenChange={open => !open && !saving && setPendingRemovals(null)}
      >
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>
              {pendingRemovals?.length === 1
                ? 'Delete this process too?'
                : 'Delete these processes too?'}
            </DialogTitle>
            <DialogDescription>
              {pendingRemovals?.length === 1
                ? 'You removed a process from this form. Its materials, equipment and staffing are not used here any more.'
                : `You removed ${pendingRemovals?.length ?? 0} processes from this form. Their materials, equipment and staffing are not used here any more.`}
            </DialogDescription>
          </DialogHeader>
          <p className='text-xs text-muted-foreground'>
            Processes used by another form, or already run on a patient, are always kept.
          </p>
          <DialogFooter className='gap-2'>
            <Button
              variant='outline'
              disabled={saving}
              onClick={() => {
                setPendingRemovals(null)
                // Saves the form as edited; the processes simply stay behind.
                void runSave([])
              }}
            >
              Keep
            </Button>
            <Button
              variant='destructive'
              disabled={saving}
              onClick={() => {
                const removals = pendingRemovals ?? []
                setPendingRemovals(null)
                void runSave(removals)
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved changes, and the author picked another version to view (AC2.7):
          discard and continue, or stay. Never discarded without this. */}
      <Dialog open={!!pendingVersionId} onOpenChange={o => !o && setPendingVersionId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogDescription>
              This form has changes that are not saved. Viewing another version discards them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setPendingVersionId(null)}>
              Cancel and stay
            </Button>
            <Button
              variant='destructive'
              onClick={() => {
                const target = pendingVersionId
                setPendingVersionId(null)
                // Re-read the saved version so the discarded edits are really gone.
                setReloadKey(k => k + 1)
                if (target) setViewedVersion(target)
              }}
            >
              Discard and continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import a form from a JSON export (z8nrz7ep1z). Always creates a new
          form, so it never alters the one open here — but it does NAVIGATE to
          the new one, which is why the dialog is told about unsaved edits. */}
      <ImportFormDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        openFormHasUnsavedChanges={step === 'builder' && isDirty}
        onCreated={created => navigate(`/form-builder/${created.id}`)}
      />

      {/* Save as New Version — signed. Shown only when the form is in active use
          by a procedure; otherwise Save overwrites in place. */}
      <SignatureDialog
        open={versionDialogOpen}
        onOpenChange={open => !open && !saving && setVersionDialogOpen(false)}
        title='Save as New Version'
        description='This form is currently in use by one or more procedures. Saving will create a new version — the current version is preserved for existing procedures.'
        confirmLabel='Save & Sign'
        confirmDisabled={!newVersionValid}
        busy={saving}
        error={versionError}
        signerName={signerName}
        // A version event is signed like any other (z8nrz7dt3z AC9): the
        // author by default, or a colleague under their own password.
        signAs={{
          mode: 'optional',
          options: versionSigners.data,
          loading: versionSigners.isLoading,
          error: versionSigners.isError ? 'Could not load the list of signers.' : null,
          selfId: user?.userId ?? null,
        }}
        onConfirm={confirmSaveVersion}
      >
        <div className='space-y-2 text-sm'>
          <div className='flex items-center gap-2'>
            <Label htmlFor='form-new-version' className='shrink-0 text-xs'>
              Version: <strong>{currentVersionLabel}</strong> →
            </Label>
            <Input
              id='form-new-version'
              className='h-8 w-40'
              value={newVersionInput}
              onChange={e => setNewVersionInput(e.target.value)}
            />
            <span className='text-[11px] text-muted-foreground'>
              (auto-proposed; editable)
            </span>
          </div>
          {!newVersionValid && (
            <p className='text-xs text-destructive'>
              {newVersionInput.trim().length === 0
                ? 'Version name is required.'
                : `Version must differ from the current one (${currentVersionLabel}).`}
            </p>
          )}
          {crossFormRefCount > 0 && (
            // A new version is a new row: it is not the one the template pins,
            // and it does not inherit the cross-form template context. Both the
            // template wiring and these references have to be re-pointed at it
            // afterwards, so say so before signing rather than leaving the
            // author to discover it as broken refs.
            <div className='rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900'>
              <p className='font-medium'>
                This form uses {crossFormRefCount} cross-form reference
                {crossFormRefCount === 1 ? '' : 's'}.
              </p>
              <p className='mt-1'>
                The new version starts detached from its Procedure Template, so its
                cross-form template context is cleared and those references will not
                resolve. After saving, update the template to this version and set the
                context again to re-point them.
              </p>
            </div>
          )}
        </div>
      </SignatureDialog>

      {/* Loading existing definition */}
      {step === 'loading' && (
        <div className='flex flex-1 items-center justify-center'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      )}

      {/* Properties step */}
      {step === 'properties' && (
        <PropertiesStep
          formMeta={formMeta}
          category={defMeta.category}
          allowedActions={defMeta.allowedActions}
          defaultRepeatCount={defMeta.defaultRepeatCount}
          error={propertiesError}
          onFormMetaChange={patch => setFormMeta(m => ({ ...m, ...patch }))}
          onCategoryChange={handleCategoryChange}
          onAllowedActionsChange={handleAllowedActionsChange}
          onDefaultRepeatCountChange={handleDefaultRepeatCountChange}
          onContinue={handleContinueToBuilder}
          onDownloadTemplate={handleDownloadTemplate}
          onImportExcel={handleImportExcel}
          onImportJson={() => setImportOpen(true)}
          autoOpenExcel={autoOpenExcel}
          excelBusy={excelBusy}
          excelErrors={excelErrors}
          excelMissingFields={excelMissingFields}
          suggestedHandle={suggestedHandle}
          handleOverride={newFormHandle}
          onHandleChange={setNewFormHandle}
        />
      )}

      {/* Builder step — live preview (no procedure, never persisted) */}
      {step === 'builder' && previewOpen && (
        <div className='flex-1 overflow-y-auto bg-muted/30'>
          <div className={cn('py-8', FORM_PAGE_GUTTER_CLASS)}>
            <div className='mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800'>
              <Eye className='h-3.5 w-3.5 shrink-0' />
              Preview only — you can fill this in to test it, but nothing is saved.
            </div>
            <div className='rounded-xl border bg-card p-6 shadow-sm'>
              {totalFields === 0 ? (
                <p className='py-10 text-center text-sm text-muted-foreground'>
                  No fields yet — switch to Edit and add some from the palette.
                </p>
              ) : (
                <FormRenderer
                  definition={previewDefinition}
                  submission={previewSubmission}
                  currentUser={PREVIEW_USER}
                  onSubmissionUpdated={setPreviewSubmission}
                  preview
                  // Without this every {handle.fieldId} ref falls through to
                  // "⚠ deleted field", because a namespaced ref can never match
                  // a local field id. Supplies labels plus a "waiting" state, so
                  // a live upstream field reads as what will fill it.
                  crossForm={crossFormAuthoring.previewResolution}
                  tenantTimezone={selectedTenant?.timezone}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Builder step — field layout */}
      {/* Operands the import could not resolve to a field on this form. Not an
          error — the form loaded and every other operand is mapped — so it sits
          above the canvas as a dismissible notice rather than blocking. Without
          it these are invisible until someone opens the field and finds the
          operand blank, which is the exact surprise z8nrz7dt4c is about. */}
      {step === 'builder' && excelUnresolvedRefs.length > 0 && (
        <div className='mx-4 mt-3 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900'>
          <div className='flex-1 space-y-1'>
            <p className='font-medium'>
              {excelUnresolvedRefs.length === 1
                ? 'One formula operand is not on this form.'
                : `${excelUnresolvedRefs.length} formula operands are not on this form.`}
            </p>
            <p>
              The imported formulas reference{' '}
              <span className='font-mono font-medium'>
                {excelUnresolvedRefs.map(r => `{${r}}`).join(', ')}
              </span>
              , which no field here declares. Every other operand was mapped. Add the missing
              fields, or open each calculated field and point the operand at the field you meant.
            </p>
          </div>
          <Button
            variant='ghost'
            size='sm'
            className='h-6 shrink-0 px-2 text-amber-900 hover:bg-amber-100'
            onClick={() => setExcelUnresolvedRefs([])}
          >
            Dismiss
          </Button>
        </div>
      )}

      {step === 'builder' &&
        !previewOpen && (
          <OperandSuggestionProvider handle={definitionId ? defIdentity.handle : null} fields={flatFields}>
          <DragDropProvider
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className='flex flex-1 overflow-hidden'>
              {/* Left panel — field palette */}
              <FieldPalette
                onAddField={addField}
                onOpenLibrary={() => setLibraryOpen(true)}
                width={paletteW}
                collapsed={paletteCollapsed}
                onToggleCollapse={() => setPaletteCollapsed(c => !c)}
                onResizeStart={startPaletteResize}
              />
              {/* A failure in the canvas stays in the canvas: the form's state is
                  held here, above the boundary, so no edit is lost (z8nrz7e1wt). */}
              <CanvasErrorBoundary>
                <FormCanvas
                  sections={sections}
                  allFields={allFieldsFlat}
                  selectedId={selectedId}
                  onSelectField={setSelectedId}
                  onSelectSection={setSelectedId}
                  onSectionChange={updateSection}
                  onDeleteField={requestDeleteField}
                  onDeleteSection={deleteSection}
                  onSetFieldUnlockedBy={setFieldUnlockedBy}
                  pickEligibleIds={combinedPickEligibleIds}
                  onPickField={handleCombinedPick}
                  fieldLayout={defMeta.fieldLayout}
                />
              </CanvasErrorBoundary>
              {/* Right panel — shared resizable/collapsible wrapper */}
              <div
                className='relative shrink-0 border-l flex flex-col overflow-hidden'
                style={{
                  width: propsCollapsed
                    ? 40
                    : (propsW ?? (selectedField?.type === 'table' ? PROPS_TABLE_W : PROPS_DEFAULT_W)),
                }}
              >
                {/* Resize handle on left edge (only when expanded) */}
                {!propsCollapsed && (
                  <div
                    className='absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/20 z-10'
                    onMouseDown={startPropsResize}
                  />
                )}
                {propsCollapsed ? (
                  /* Collapsed strip */
                  <div className='flex flex-col items-center gap-2 pt-2'>
                    <button
                      className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent'
                      onClick={() => setPropsCollapsed(false)}
                      title='Expand panel'
                    >
                      <ChevronLeft className='h-4 w-4 text-muted-foreground' />
                    </button>
                    <button
                      className='mt-2 flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent'
                      onClick={() => setPropsCollapsed(false)}
                      title={hasSelection ? 'Field Properties' : 'Form Settings'}
                    >
                      <Settings className='h-4 w-4 text-muted-foreground' />
                    </button>
                  </div>
                ) : hasSelection ? (
                  /* Field / section properties */
                  <div className='relative flex flex-col flex-1 overflow-hidden'>
                    <button
                      className='absolute top-2.5 right-2 z-20 flex h-6 w-6 items-center justify-center rounded-md hover:bg-accent'
                      onClick={() => setPropsCollapsed(true)}
                      title='Collapse panel'
                    >
                      <ChevronRight className='h-3.5 w-3.5 text-muted-foreground' />
                    </button>
                    <PropertiesPanel
                      field={selectedField}
                      section={selectedSection}
                      formMeta={formMeta}
                      allFields={allFieldsFlat}
                      allSections={sections}
                      onFieldChange={updateField}
                      onSectionChange={updateSection}
                      onSectionDelete={deleteSection}
                      onFormMetaChange={patch => setFormMeta(m => ({ ...m, ...patch }))}
                      visibilityPick={visibilityPick}
                      onVisibilityPickChange={setVisibilityPick}
                      notificationPick={notificationPick}
                      onNotificationPickChange={setNotificationPick}
                      crossForm={crossFormAuthoring}
                      formInUse={inUse === true}
                      formFieldLayout={defMeta.fieldLayout}
                    />
                  </div>
                ) : (
                  /* Form Settings */
                  <div className='flex flex-col flex-1 overflow-hidden'>
                    <div className='border-b px-4 py-3 flex items-center gap-2 shrink-0'>
                      <div className='flex-1'>
                        <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                          Form Settings
                        </p>
                        <p className='mt-0.5 text-[11px] text-muted-foreground'>
                          Saving keeps you in the builder — use Close when you&apos;re done.
                        </p>
                      </div>
                      <button
                        className='flex h-6 w-6 items-center justify-center rounded-md hover:bg-accent shrink-0'
                        onClick={() => setPropsCollapsed(true)}
                        title='Collapse panel'
                      >
                        <ChevronRight className='h-3.5 w-3.5 text-muted-foreground' />
                      </button>
                    </div>
                    <div className='overflow-y-auto flex-1 p-4'>
                      <FormDefinitionPanel
                        formMeta={formMeta}
                        sections={sections}
                        category={defMeta.category}
                        allowedActions={defMeta.allowedActions}
                        defaultRepeatCount={defMeta.defaultRepeatCount}
                        onDefaultRepeatCountChange={handleDefaultRepeatCountChange}
                        completionGate={defMeta.completionGate}
                        completerGroupIds={defMeta.completerGroupIds ?? []}
                        onCompleterGroupIdsChange={handleCompleterGroupIdsChange}
                        fieldLayout={defMeta.fieldLayout ?? null}
                        onFieldLayoutChange={fieldLayout => setDefMeta(prev => ({ ...prev, fieldLayout }))}
                        definitionId={definitionId}
                        onFormMetaChange={patch => setFormMeta(m => ({ ...m, ...patch }))}
                        onCategoryChange={handleCategoryChange}
                        onAllowedActionsChange={handleAllowedActionsChange}
                        onCompletionGateChange={handleCompletionGateChange}
                        onSaved={(def: FormDefinition) => {
                          // Adopt what the server persisted so the toolbar badge
                          // and later toolbar saves reflect this panel save.
                          setDefMeta({
                            category: def.category,
                            allowedActions: def.allowedActions ?? [],
                            completionGate: def.completionGate ?? undefined,
                            completerGroupIds: def.completerGroupIds ?? [],
                            fieldLayout: sanitizeFieldLayout(def.fieldLayout),
                          })
                          // Stay in the builder; a first save adopts the created
                          // id so later saves update in place (see handleSaveForm).
                          if (!definitionId) navigate(`/form-builder/${def.id}`, { replace: true })
                        }}
                      />
                      <p className='mt-4 border-t pt-4 text-xs leading-relaxed text-muted-foreground'>
                        Click a section or field on the canvas to edit its properties.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <DragOverlay>
              {source => {
                const data = source.data as
                  | { origin?: string; fieldType?: FieldType; libraryField?: FieldDefinition }
                  | undefined
                if (!data) return null
                if (data.origin === 'library' && data.libraryField) {
                  return (
                    <div className='flex min-w-48 cursor-grabbing items-center gap-2.5 rounded-lg border bg-card px-3 py-2.5 text-sm opacity-90 shadow-xl'>
                      <Library className='h-3.5 w-3.5 text-muted-foreground' />
                      <span className='flex-1 font-medium'>
                        {data.libraryField.label || 'Untitled'}
                      </span>
                      <Badge variant='outline' className='text-xs'>
                        {data.libraryField.fieldType}
                      </Badge>
                    </div>
                  )
                }
                if (data.origin === 'palette' && data.fieldType) {
                  const Icon = FIELD_ICONS[data.fieldType]
                  return (
                    <div className='flex cursor-grabbing items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm font-medium shadow-xl'>
                      <Icon className='h-4 w-4 text-muted-foreground' />
                      {FIELD_LABELS[data.fieldType]}
                    </div>
                  )
                }
                if (data.origin === 'field') {
                  const f = allFieldsFlat.find(x => x.id === String(source.id))
                  if (!f) return null
                  const Icon = FIELD_ICONS[f.type]
                  return (
                    <div className='flex min-w-48 cursor-grabbing items-center gap-2.5 rounded-lg border bg-card px-3 py-2.5 text-sm opacity-90 shadow-xl'>
                      <Icon className='h-3.5 w-3.5 text-muted-foreground' />
                      <span className='flex-1 font-medium'>{f.label || 'Untitled'}</span>
                      <Badge variant='outline' className='text-xs'>
                        {FIELD_LABELS[f.type]}
                      </Badge>
                    </div>
                  )
                }
                if (data.origin === 'section') {
                  const id = String(source.id).replace(/^section:/, '')
                  const sec = sections.find(s => s.id === id)
                  if (!sec) return null
                  return (
                    <div className='flex min-w-48 cursor-grabbing items-center gap-2.5 rounded-lg border bg-card px-3 py-2.5 text-sm opacity-90 shadow-xl'>
                      <Rows3 className='h-3.5 w-3.5 text-muted-foreground' />
                      <span className='flex-1 font-medium'>{sec.title || 'Untitled section'}</span>
                      <Badge variant='outline' className='text-xs'>
                        Section
                      </Badge>
                    </div>
                  )
                }
                return null
              }}
            </DragOverlay>

            {libraryOpen && (
              <LibraryFieldPicker
                formFields={sections.flatMap(s => s.fields)}
                sections={sections.map(s => ({ id: s.id, title: s.title || 'Untitled section' }))}
                defaultSectionId={activeSectionId}
                onInsert={insertLibraryFields}
                onClose={() => setLibraryOpen(false)}
                crossForm={crossFormAuthoring}
              />
            )}
          </DragDropProvider>
          </OperandSuggestionProvider>
        )}
    </div>
  )
}

// ─── Library field picker ──────────────────────────────────────────────────────

/**
 * The step filter's "no step" option. Radix Select refuses an empty-string
 * value, so the cleared state needs a sentinel; it never reaches the server.
 */
const ANY_STEP = '__any_step'

/** A small type-filter facet chip for the library picker. */
function FacetChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={
        'rounded-full border px-2.5 py-0.5 text-xs capitalize transition-colors ' +
        (active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:bg-accent')
      }
    >
      {label} <span className='opacity-60'>{count}</span>
    </button>
  )
}

/**
 * One operand of a calculated field's formula, resolved against the form being
 * edited. `resolved` false is the only case that costs the author work later.
 */
interface OperandStatus {
  ref: string
  resolved: boolean
  note: string
}

/**
 * Where each `{token}` in a library formula would land on this form.
 *
 * The "likely" case reuses `guessOperandMapping`, the same helper that pre-fills
 * the re-map dialog, so what a row predicts and what the dialog then proposes
 * cannot drift apart.
 */
function describeOperands(formula: string, pickableFields: FormField[]): OperandStatus[] {
  return extractFormulaRefs(formula).map(ref => {
    // Already fully qualified ({handle.fieldId}): insert keeps these verbatim.
    if (isCrossFormRef(ref)) return { ref, resolved: true, note: 'cross-form reference, kept as-is' }
    if (pickableFields.some(f => f.id === ref)) return { ref, resolved: true, note: 'on this form' }
    const guessId = guessOperandMapping([ref], pickableFields)[ref]
    const guess = guessId ? pickableFields.find(f => f.id === guessId) : undefined
    return guess
      ? { ref, resolved: true, note: `likely "${guess.label || 'Untitled'}"` }
      : { ref, resolved: false, note: 'not on this form' }
  })
}

/** A single library result row. Draggable onto the canvas (origin: 'library')
 *  unless it's a calculated field with formula operands — those still require the
 *  click "Re-map & Insert" flow, since a drop can't surface the re-map dialog.
 *
 *  Calculated rows expand to show the formula READ-ONLY. Expanding is inert: it
 *  inserts nothing, opens no dialog, and touches no search/facet/page state. */
function LibraryFieldItem({
  def,
  onPick,
  pickableFields,
  expanded,
  onToggleExpand,
  stepLabel,
  insertedInto,
  error,
  confirming,
  onConfirmAgain,
  onCancelAgain,
}: {
  /** A list row; `stepMatch` is present only while a step filter is active. */
  def: FieldLibraryListItem
  onPick: (def: FieldDefinition) => void
  /** The section this field was last inserted into in this picker session, if it was. */
  insertedInto?: string
  /** Why the last insert from this row failed. */
  error?: string
  /** Asking whether to insert this field a second time. */
  confirming: boolean
  onConfirmAgain: () => void
  onCancelAgain: () => void
  /** Valid re-map targets on this form: the same set the re-map dialog offers. */
  pickableFields: FormField[]
  expanded: boolean
  onToggleExpand: (id: string) => void
  /** The step the list is filtered to, for the per-row "why it matched" line. */
  stepLabel?: string
}) {
  const hasFormulaRefs =
    def.fieldType === 'calculated' &&
    !!def.formula &&
    extractFormulaRefs(def.formula).length > 0
  const draggable = !hasFormulaRefs

  const isCalculated = def.fieldType === 'calculated'
  // Prisma's `String?` arrives as null, so normalize before testing for content.
  const formula = def.formula?.trim() ?? ''
  const operands = isCalculated && expanded && formula ? describeOperands(formula, pickableFields) : []

  const { ref, isDragging } = useDraggable({
    id: `library-${def.id}`,
    data: { origin: 'library', libraryField: def },
    disabled: !draggable,
  })

  return (
    <li
      ref={draggable ? ref : undefined}
      className={cn(
        'px-3 py-2',
        draggable && 'cursor-grab select-none',
        isDragging && 'opacity-40',
      )}
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-1.5'>
          {/* Calculated rows only. A non-calculated field has no formula to read,
              so it gets no control at all rather than a disabled one. */}
          {isCalculated && (
            <button
              type='button'
              onClick={() => onToggleExpand(def.id)}
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Hide' : 'Show'} the formula for ${def.label}`}
              className='shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
            >
              {expanded ? (
                <ChevronDown className='h-4 w-4' />
              ) : (
                <ChevronRight className='h-4 w-4' />
              )}
            </button>
          )}
          <div className='min-w-0'>
            <p className='flex items-center gap-1.5 text-sm font-medium'>
              <span className='truncate'>{def.label}</span>
              {insertedInto && (
                <span className='inline-flex shrink-0 items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10.5px] font-medium text-emerald-700'>
                  <Check className='h-3 w-3' />
                  Inserted
                </span>
              )}
            </p>
            <p className='text-xs text-muted-foreground'>
              {def.fieldType}
              {/* Read from the payload, not inferred from the active filter, so
                  the reason can grow to "intended" without a client change. */}
              {def.stepMatch === 'used' && (
                <>
                  {' · '}
                  <span className='text-primary'>
                    used by {stepLabel ?? 'this step'}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
        <Button size='sm' variant='outline' onClick={() => onPick(def)}>
          <Plus className='mr-1.5 h-3.5 w-3.5' />
          {hasFormulaRefs ? 'Re-map & Insert' : 'Insert'}
        </Button>
      </div>
      {hasFormulaRefs && (
        <p className='mt-1 text-[11px] text-amber-700'>
          Contains formula operands — you'll map them to fields on this form next.
        </p>
      )}
      {confirming && (
        <div role='alert' className='mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900'>
          <span className='min-w-0 flex-1'>
            "{def.label}" is already in {insertedInto}. Insert another copy?
          </span>
          <Button type='button' size='sm' variant='ghost' className='h-6 px-2 text-xs' onClick={onCancelAgain}>
            Cancel
          </Button>
          <Button type='button' size='sm' className='h-6 px-2 text-xs' onClick={onConfirmAgain}>
            Insert again
          </Button>
        </div>
      )}
      {error && (
        <p role='alert' className='mt-1 text-[11px] text-destructive'>
          {error}
        </p>
      )}
      {isCalculated && expanded && (
        // Same chrome as the re-map dialog's "Original formula" box, so the
        // expression reads identically in both places.
        <div className='mt-2 rounded-md border bg-muted/40 px-3 py-2'>
          <p className='mb-1 text-xs font-medium text-muted-foreground'>Formula</p>
          {formula ? (
            <code className='break-all text-xs'>{formula}</code>
          ) : (
            <p className='text-xs text-muted-foreground'>No formula defined</p>
          )}
          {operands.length > 0 && (
            <div className='mt-2 border-t pt-2'>
              <p className='mb-1 text-xs font-medium text-muted-foreground'>Operands</p>
              <ul className='flex flex-col gap-1'>
                {operands.map(o => (
                  <li key={o.ref} className='text-[11px] leading-tight'>
                    <code className='rounded bg-muted px-1 py-0.5'>{`{${o.ref}}`}</code>{' '}
                    <span className={o.resolved ? 'text-muted-foreground' : 'text-amber-700'}>
                      {o.note}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  )
}

/**
 * Best-guess operand → form-field prefill for the re-map step. Matches the
 * placeholder name against field labels after normalizing both to lowercase
 * alphanumerics, so `{weight}` finds "Patient Weight (kg)". Substring matches
 * require ≥3 chars to avoid false positives; unmatched refs stay unmapped.
 */
function guessOperandMapping(refs: string[], fields: FormField[]): Record<string, string> {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '')
  const out: Record<string, string> = {}
  for (const ref of refs) {
    const r = norm(ref)
    if (!r) continue
    const hit = fields.find(f => {
      const l = norm(f.label || '')
      if (!l) return false
      if (l === r) return true
      if (r.length >= 3 && l.includes(r)) return true
      return l.length >= 3 && r.includes(l)
    })
    if (hit) out[ref] = hit.id
  }
  return out
}

/**
 * Two-step picker:
 *   1. List mode — a draggable, non-modal floating panel: search the library,
 *      filter by type, page through results, click Insert or drag onto the canvas.
 *   2. Re-map mode (calculated fields only) — reassign each formula operand to a
 *      field currently on this form before the copy is inserted.
 */
function LibraryFieldPicker({
  formFields,
  sections,
  defaultSectionId,
  onInsert,
  onClose,
  crossForm,
}: {
  formFields: FormField[]
  /** The form's sections, in order: where an insert can land. */
  sections: { id: string; title: string }[]
  /** The section active on the canvas when the picker opened. */
  defaultSectionId: string | null
  /** Appends copies to the section; returns why nothing was inserted, or null. */
  onInsert: (defs: FieldDefinition[], sectionId: string | null, opts?: { formula?: string }) => string | null
  onClose: () => void
  /** Cross-form authoring — lets re-map targets point at earlier forms. */
  crossForm?: CrossFormAuthoring
}) {
  // 'fields' = search individual library fields; 'categories' = insert a whole category.
  const [mode, setMode] = useState<'fields' | 'categories'>('fields')

  // The picker stays open across inserts (z8nrz7dewa). Everything below is
  // local, so closing (which unmounts it) ends the session and clears it.
  // Target section: picked here, else the one active when the picker opened
  // (or, if that one was deleted, the last). Null only when there are none.
  const [pickedSectionId, setPickedSectionId] = useState<string | null>(defaultSectionId)
  const targetSection =
    sections.find(s => s.id === pickedSectionId) ??
    sections.find(s => s.id === defaultSectionId) ??
    sections[sections.length - 1] ??
    null
  // Successful inserts this session, in click order.
  const [inserted, setInserted] = useState<{ key: number; libraryFieldId: string; label: string; section: string }[]>([])
  const lastInsertedInto = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of inserted) m.set(r.libraryFieldId, r.section)
    return m
  }, [inserted])
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  /** Inserts into the target section; records it, or shows why not on the row. */
  function insert(defs: FieldDefinition[], opts?: { formula?: string }, rowId?: string): boolean {
    const error = onInsert(defs, targetSection?.id ?? null, opts)
    if (rowId) {
      setRowErrors(prev => {
        const next = { ...prev }
        if (error) next[rowId] = error
        else delete next[rowId]
        return next
      })
    }
    if (error) return false
    const section = targetSection?.title ?? ''
    setInserted(prev => [
      ...prev,
      ...defs.map((d, i) => ({ key: prev.length + i, libraryFieldId: d.id, label: d.label, section })),
    ])
    return true
  }

  // Search + type filter + pagination
  const PICKER_TAKE = 8
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [fieldType, setFieldType] = useState<CgtFieldType | undefined>(undefined)
  const [skip, setSkip] = useState(0)

  // Process-step filter: narrows to fields USED BY a step (a Process field, by
  // its key). Independent state, so clearing it leaves `q` and `fieldType` as
  // they were. The options come from saved forms, not from the library.
  const [processStepId, setProcessStepId] = useState<string | undefined>(undefined)
  const steps = useProcessStepOptions()
  // Steps already on the form being edited go first: "the step I am building"
  // is the one an author reaches for most.
  const ownStepIds = useMemo(
    () => new Set(formFields.filter(f => f.type === 'process').map(f => f.libraryFieldId ?? f.id)),
    [formFields],
  )
  const stepOptions = useMemo(() => {
    const all = steps.data ?? []
    return {
      onThisForm: all.filter(s => ownStepIds.has(s.fieldId)),
      elsewhere: all.filter(s => !ownStepIds.has(s.fieldId)),
    }
  }, [steps.data, ownStepIds])
  const selectedStep = processStepId ? steps.data?.find(s => s.fieldId === processStepId) : undefined

  // Which calculated rows have their formula open. A Set, so several stay open at
  // once: comparing two same-named fields side by side is the whole point.
  //
  // Deliberately local to the picker: the mount site is `{libraryOpen && <…/>}`,
  // so closing unmounts this and the expansion state dies with it. Lifting it to
  // the page would make expansions survive a reopen.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })
  }, [])

  // Categories mode — list categories and bulk-insert a category's members.
  const { can } = usePermission()
  const canReadCategories = can(PERMISSIONS.fieldCategoriesRead)
  const categoriesList = useFieldCategoriesList({ take: 200 }, canReadCategories)
  const allCategories = categoriesList.data?.items ?? []
  const [categoryQuery, setCategoryQuery] = useState('')
  const categoryQ = categoryQuery.trim().toLowerCase()
  const categories = categoryQ
    ? allCategories.filter(c => c.name.toLowerCase().includes(categoryQ))
    : allCategories
  const [insertingCategoryId, setInsertingCategoryId] = useState<string | null>(null)
  const [categoryError, setCategoryError] = useState<string | null>(null)
  // Informational notice when calculated fields with formula refs are skipped.
  const [skippedNotice, setSkippedNotice] = useState<string | null>(null)

  async function handleInsertCategory(id: string) {
    setCategoryError(null)
    setSkippedNotice(null)
    setInsertingCategoryId(id)
    try {
      const detail = await fieldCategoriesApi.get(id)
      // Calculated members whose formula references other fields by ID cannot be
      // bulk-inserted safely — the operand IDs don't exist on this form yet and
      // the field would silently render "—". Skip them and surface a clear notice.
      const danglingCalc = (m: typeof detail.members[0]) =>
        m.fieldType === 'calculated' && extractFormulaRefs(m.formula ?? '').length > 0
      const safe = detail.members.filter(m => !danglingCalc(m))
      const skippedCount = detail.members.length - safe.length
      if (safe.length > 0) {
        const error = onInsert(safe, targetSection?.id ?? null)
        if (error) {
          setCategoryError(error)
          return
        }
        setInserted(prev => [
          ...prev,
          ...safe.map((d, i) => ({ key: prev.length + i, libraryFieldId: d.id, label: d.label, section: targetSection?.title ?? '' })),
        ])
      }
      if (skippedCount > 0) {
        setSkippedNotice(
          `${skippedCount} calculated field${skippedCount === 1 ? ' was' : 's were'} skipped — insert ${skippedCount === 1 ? 'it' : 'them'} individually to map formula operands.`,
        )
      }
    } catch (e) {
      setCategoryError(e instanceof Error ? e.message : 'Could not load category members.')
    } finally {
      setInsertingCategoryId(null)
    }
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300)
    return () => clearTimeout(t)
  }, [q])

  const list = useFieldLibraryList({
    q: debouncedQ || undefined,
    fieldType,
    processStepId,
    // Only offer enabled fields for insertion; disabled ones stay in the
    // library but drop out of new-form authoring.
    enabled: true,
    // Nor should the palette offer the e2e suite's leftovers — inserting
    // "Canvas process 006adea0" into a real form is never the intent.
    isTestData: false,
    skip,
    take: PICKER_TAKE,
  })
  const total = list.data?.total ?? 0
  const typeCounts = list.data?.typeCounts ?? {}
  // typeCounts reflects the search only (not the selected type), so chips stay stable.
  const allCount = Object.values(typeCounts).reduce((a, b) => a + b, 0)

  // Clamp the page if a filter shrank the result set below the current offset.
  useEffect(() => {
    if (total > 0 && skip >= total) {
      setSkip(Math.floor((total - 1) / PICKER_TAKE) * PICKER_TAKE)
    }
  }, [total, skip])

  // Re-map step state — only set when user picks a calculated field with refs
  const [remapping, setRemapping] = useState<FieldDefinition | null>(null)
  // opMapping: oldOperandId → form field id to replace it with
  const [opMapping, setOpMapping] = useState<Record<string, string>>({})
  const [remapError, setRemapError] = useState<string | null>(null)

  // Form fields available as re-map targets.
  // Exclude section (no value), calculated (computed, no submission value),
  // and signature (no numeric value) — mapping to these makes the formula silently render "—".
  const pickableFields = formFields.filter(
    f => f.type !== 'section' && f.type !== 'calculated' && f.type !== 'signature',
  )
  // Fields from earlier forms in the workflow — a placeholder may map to one of
  // these instead (stored as {handle.fieldId}); labels already carry the form name.
  const upstreamPickableFields = crossForm?.available ? crossForm.allUpstreamFields : []

  function handlePickItem(def: FieldDefinition, again = false) {
    // A field already inserted this session asks first; a second copy is never blocked outright.
    if (!again && lastInsertedInto.has(def.id)) {
      setConfirmingId(def.id)
      return
    }
    setConfirmingId(null)
    if (!targetSection) {
      setRowErrors(prev => ({ ...prev, [def.id]: 'Select a target section first.' }))
      return
    }
    if (def.fieldType === 'calculated' && def.formula) {
      // Cross-form refs ({handle.fieldId}, e.g. from an Excel import) are
      // already fully qualified — only free-named placeholders need mapping.
      const mappable = extractFormulaRefs(def.formula).filter(r => !isCrossFormRef(r))
      if (mappable.length > 0) {
        setRemapping(def)
        // Pre-fill with best-guess matches (placeholder name vs field label) so
        // e.g. {weight} lands on "Patient Weight (kg)" — the admin can override.
        setOpMapping(guessOperandMapping(mappable, pickableFields))
        setRemapError(null)
        return
      }
    }
    // Non-calculated, no refs, or only fully-qualified cross-form refs → insert directly
    insert([def], undefined, def.id)
  }

  function handleConfirmRemap() {
    if (!remapping || !remapping.formula) return
    const allRefs = extractFormulaRefs(remapping.formula)
    // Cross-form refs stay verbatim; only free placeholders require a mapping.
    const mappable = allRefs.filter(r => !isCrossFormRef(r))
    // Treat empty/whitespace-only selections as unmapped
    const unmapped = mappable.filter(r => !opMapping[r]?.trim())
    if (unmapped.length > 0) {
      setRemapError(
        `Map all ${mappable.length} operand${mappable.length === 1 ? '' : 's'} before inserting (${unmapped.length} remaining).`,
      )
      return
    }
    // Single-pass regex rewrite — order-independent, handles duplicate tokens correctly
    const newFormula = remapping.formula.replace(
      /\{([^}]+)\}/g,
      (_m, id) => (opMapping[id] ? `{${opMapping[id]}}` : `{${id}}`),
    )
    // Sanity check: every original ref should now map to a target ref
    if (extractFormulaRefs(newFormula).length !== allRefs.length) {
      setRemapError('Formula rewrite produced an unexpected result. Please try again.')
      return
    }
    // Back to the still-open list either way; a failure shows on the row.
    insert([remapping], { formula: newFormula }, remapping.id)
    setRemapping(null)
    setRemapError(null)
  }

  // Floating-panel drag — moved by dragging the header bar only.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const dragOffset = useRef<{ x: number; y: number } | null>(null)

  function handleHeaderPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    // Don't start a panel drag from the close button.
    if ((e.target as HTMLElement).closest('button')) return
    const panel = e.currentTarget.parentElement
    if (!panel) return
    const rect = panel.getBoundingClientRect()
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    setPos({ x: rect.left, y: rect.top })
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function handleHeaderPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragOffset.current) return
    // If the button was released outside our up handler (pointercancel / lost
    // capture), stop tracking so the panel doesn't follow a button-less hover.
    if (e.buttons === 0) {
      dragOffset.current = null
      return
    }
    // Clamp so the header (the only drag/close affordance) can't be lost off-screen.
    setPos({
      x: Math.min(Math.max(e.clientX - dragOffset.current.x, 0), window.innerWidth - 80),
      y: Math.min(Math.max(e.clientY - dragOffset.current.y, 0), window.innerHeight - 40),
    })
  }

  function handleHeaderPointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragOffset.current) return
    dragOffset.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  // ── Re-map step — a dialog over the still-open list, which keeps its place ──
  const remapDialog = (() => {
    if (!remapping) return null
    const allRemapRefs = extractFormulaRefs(remapping.formula ?? '')
    // Only free placeholders need mapping; qualified cross-form refs are kept.
    const refs = allRemapRefs.filter(r => !isCrossFormRef(r))
    const keptCrossRefs = allRemapRefs.filter(r => isCrossFormRef(r))
    return (
      // Dismissing it cancels the re-map only: nothing is inserted, the picker stays.
      <Dialog
        open
        onOpenChange={o => {
          if (!o) {
            setRemapping(null)
            setRemapError(null)
          }
        }}
      >
        <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Re-map formula operands</DialogTitle>
            <DialogDescription>
              "{remapping.label}" is a calculated field. Its formula references fields by ID that
              don't exist on this form yet. Map each operand to a field on this form
              {upstreamPickableFields.length > 0 ? ' — or to a field on an earlier form in the workflow — ' : ' '}
              before inserting.
            </DialogDescription>
          </DialogHeader>

          <div className='rounded-md border bg-muted/40 px-3 py-2'>
            <p className='mb-1 text-xs font-medium text-muted-foreground'>Original formula</p>
            <code className='break-all text-xs'>{remapping.formula}</code>
          </div>

          {keptCrossRefs.length > 0 && (
            <p className='text-xs text-muted-foreground'>
              {keptCrossRefs.length === 1 ? 'One operand is' : `${keptCrossRefs.length} operands are`}{' '}
              already qualified cross-form reference{keptCrossRefs.length === 1 ? '' : 's'} (
              {keptCrossRefs.map(r => `{${r}}`).join(', ')}) and will be kept as-is.
            </p>
          )}

          <div className='flex flex-col gap-3'>
            {refs.map(oldId => (
              <div key={oldId} className='flex flex-col gap-1'>
                <Label className='text-xs font-medium'>
                  Operand <code className='rounded bg-muted px-1 py-0.5'>{`{${oldId}}`}</code>
                </Label>
                <Select
                  value={opMapping[oldId] ?? ''}
                  onValueChange={v =>
                    setOpMapping(prev => ({ ...prev, [oldId]: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select a form field…' />
                  </SelectTrigger>
                  <SelectContent>
                    {pickableFields.length === 0 && upstreamPickableFields.length === 0 ? (
                      <SelectItem value='__empty' disabled>
                        No fields on this form yet
                      </SelectItem>
                    ) : (
                      <>
                        {pickableFields.map(f => (
                          <SelectItem key={f.id} value={f.id}>
                            {f.label || 'Untitled'}{' '}
                            <span className='text-muted-foreground'>({f.type})</span>
                          </SelectItem>
                        ))}
                        {upstreamPickableFields.length > 0 && (
                          <SelectItem value='__upstream_header' disabled>
                            — From earlier forms —
                          </SelectItem>
                        )}
                        {upstreamPickableFields.map(f => (
                          <SelectItem key={f.refKey} value={f.refKey}>
                            {f.label}{' '}
                            <span className='text-muted-foreground'>({f.fieldType})</span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {remapError && <p className='text-sm text-destructive'>{remapError}</p>}

          <DialogFooter>
            <Button
              type='button'
              variant='ghost'
              onClick={() => {
                setRemapping(null)
                setRemapError(null)
              }}
            >
              Cancel
            </Button>
            <Button
              type='button'
              disabled={pickableFields.length === 0 && upstreamPickableFields.length === 0}
              onClick={handleConfirmRemap}
            >
              Confirm & Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  })()

  // ── List step — draggable, non-modal floating panel ─────────────────────────
  return (
    <>
    <div
      ref={panelRef}
      role='dialog'
      aria-label='Field Library'
      // Esc closes the picker, but not when it was meant for a dropdown inside it
      // (those render outside the panel) or the re-map dialog.
      onKeyDown={e => {
        if (e.key === 'Escape' && !remapping && panelRef.current?.contains(e.target as Node)) onClose()
      }}
      className='fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-7rem)] w-[32rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg border bg-card shadow-xl'
      style={pos ? { left: pos.x, top: pos.y, right: 'auto', transform: 'none' } : undefined}
    >
      {/* Header — drag handle (drag to move the panel) + close */}
      <div
        onPointerDown={handleHeaderPointerDown}
        onPointerMove={handleHeaderPointerMove}
        onPointerUp={handleHeaderPointerUp}
        onPointerCancel={handleHeaderPointerUp}
        className='flex shrink-0 cursor-grab touch-none select-none items-center gap-2 border-b bg-muted/40 px-3 py-2 active:cursor-grabbing'
      >
        <GripVertical className='h-4 w-4 shrink-0 text-muted-foreground' />
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-semibold leading-tight'>Field Library</p>
          <p className='truncate text-[11px] text-muted-foreground'>
            {inserted.length > 0
              ? `${inserted.length} ${inserted.length === 1 ? 'field' : 'fields'} inserted`
              : 'Pick saved fields to add to this form.'}
          </p>
        </div>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className='h-7 w-7 shrink-0'
          onClick={() => setCollapsed(c => !c)}
          aria-label={collapsed ? 'Expand the Field Library' : 'Collapse the Field Library'}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronDown className='h-4 w-4' /> : <ChevronUp className='h-4 w-4' />}
        </Button>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className='h-7 w-7 shrink-0'
          onClick={onClose}
          aria-label='Close the Field Library'
          title='Close'
        >
          <X className='h-4 w-4' />
        </Button>
      </div>

      {/* Where inserts land: always shown, switchable without closing. */}
      <div className={cn('flex shrink-0 items-center gap-2 border-b px-3 py-2', collapsed && 'hidden')}>
        <Label className='shrink-0 text-xs text-muted-foreground'>Insert into</Label>
        {targetSection ? (
          <Select value={targetSection.id} onValueChange={setPickedSectionId}>
            <SelectTrigger aria-label='Target section' className='h-8 text-sm'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sections.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className='text-xs text-destructive'>No sections yet. Add a section to the form first.</span>
        )}
      </div>

      {/* Body. Collapsed by height, not unmounted, so search, page and scroll survive. */}
      <div className={cn('flex flex-1 flex-col gap-2 overflow-y-auto p-3', collapsed && 'max-h-0 flex-none !p-0')}>
        {/* Mode toggle — individual fields vs. whole category (hidden without read permission). */}
        {canReadCategories && (
          <div className='flex gap-1 rounded-md border p-0.5'>
            <button
              type='button'
              onClick={() => setMode('fields')}
              className={cn(
                'flex-1 rounded px-2 py-1 text-xs font-medium transition-colors',
                mode === 'fields'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent',
              )}
            >
              Fields
            </button>
            <button
              type='button'
              onClick={() => setMode('categories')}
              className={cn(
                'flex-1 rounded px-2 py-1 text-xs font-medium transition-colors',
                mode === 'categories'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent',
              )}
            >
              Categories
            </button>
          </div>
        )}

        {mode === 'categories' && (
          <>
            {categoriesList.isLoading && (
              <div className='flex items-center justify-center py-10'>
                <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
              </div>
            )}
            {categoriesList.isError && (
              <p className='text-sm text-destructive'>
                Could not load categories. {(categoriesList.error as Error).message}
              </p>
            )}
            {categoryError && <p className='text-sm text-destructive'>{categoryError}</p>}
            {skippedNotice && (
              <p className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800'>
                {skippedNotice}
              </p>
            )}
            {categoriesList.data && allCategories.length > 6 && (
              <div className='relative'>
                <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  value={categoryQuery}
                  placeholder='Search categories…'
                  onChange={e => setCategoryQuery(e.target.value)}
                  className='pl-9'
                />
              </div>
            )}
            {categoriesList.data && allCategories.length === 0 && (
              <p className='py-6 text-center text-sm text-muted-foreground'>
                No categories yet — create one in the field library to group related fields.
              </p>
            )}
            {categoriesList.data && allCategories.length > 0 && categories.length === 0 && (
              <p className='py-6 text-center text-sm text-muted-foreground'>
                No categories match “{categoryQuery.trim()}”.
              </p>
            )}
            {categories.length > 0 && (
              <ul className='divide-y rounded-md border'>
                {categories.map(c => (
                  <li key={c.id} className='flex items-center justify-between gap-3 px-3 py-2'>
                    <div className='min-w-0'>
                      <p className='truncate text-sm font-medium'>{c.name}</p>
                      <p className='text-xs text-muted-foreground'>
                        {c.fieldCount} field{c.fieldCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <Button
                      size='sm'
                      variant='outline'
                      disabled={c.fieldCount === 0 || insertingCategoryId === c.id}
                      onClick={() => void handleInsertCategory(c.id)}
                    >
                      {insertingCategoryId === c.id ? (
                        <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
                      ) : (
                        <Plus className='mr-1.5 h-3.5 w-3.5' />
                      )}
                      Insert all
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {mode === 'fields' && (
          <>
        <Input
          placeholder='Search fields…'
          value={q}
          onChange={e => {
            setQ(e.target.value)
            setSkip(0)
          }}
        />

        {/* Process-step filter. Outside the `allCount > 0` guard below on purpose:
            a step with no fields zeroes every facet, and the control that
            clears the filter has to stay on screen for that exact case. */}
        {steps.data && steps.data.length > 0 && (
          <Select
            value={processStepId ?? ANY_STEP}
            onValueChange={v => {
              setProcessStepId(v === ANY_STEP ? undefined : v)
              setSkip(0)
            }}
          >
            <SelectTrigger aria-label='Filter by process step'>
              <SelectValue placeholder='Any process step' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_STEP}>Any process step</SelectItem>
              {stepOptions.onThisForm.length > 0 && (
                <>
                  <SelectItem value='__own_header' disabled>
                    On this form
                  </SelectItem>
                  {stepOptions.onThisForm.map(s => (
                    <SelectItem key={s.fieldId} value={s.fieldId}>
                      {s.label}
                    </SelectItem>
                  ))}
                  {stepOptions.elsewhere.length > 0 && (
                    <SelectItem value='__other_header' disabled>
                      Other forms
                    </SelectItem>
                  )}
                </>
              )}
              {stepOptions.elsewhere.map(s => (
                <SelectItem key={s.fieldId} value={s.fieldId}>
                  {s.label}{' '}
                  <span className='text-muted-foreground'>
                    ({s.formName}
                    {s.formCount > 1 ? ` +${s.formCount - 1}` : ''})
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Type-filter facets — mirrors the Field Library tab. */}
        {allCount > 0 && (
          <div className='flex flex-wrap gap-1'>
            <FacetChip
              label='All'
              count={allCount}
              active={!fieldType}
              onClick={() => {
                setFieldType(undefined)
                setSkip(0)
              }}
            />
            {(Object.keys(typeCounts) as CgtFieldType[])
              .filter(t => typeCounts[t] > 0)
              .map(t => (
                <FacetChip
                  key={t}
                  label={t}
                  count={typeCounts[t]}
                  active={fieldType === t}
                  onClick={() => {
                    setFieldType(t)
                    setSkip(0)
                  }}
                />
              ))}
          </div>
        )}

        {list.isLoading && (
          <div className='flex items-center justify-center py-10'>
            <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
          </div>
        )}
        {list.isError && (
          <p className='text-sm text-destructive'>
            Could not load the field library. {(list.error as Error).message}
          </p>
        )}
        {list.data && list.data.items.length === 0 && (
          <p className='py-6 text-center text-sm text-muted-foreground'>
            {/* `allCount` ignores the type chip, so zero here with a step and no
                search text means the step has no fields at all. Say that, not
                "match your filters": the chip strip hides itself when every
                count is zero, so an active chip would be invisible. */}
            {processStepId && !debouncedQ && allCount === 0
              ? 'No fields are used by this step yet.'
              : debouncedQ || fieldType || processStepId
                ? 'No fields match your filters.'
                : 'No fields in the library yet.'}
          </p>
        )}
        {list.data && list.data.items.length > 0 && (
          <ul className='divide-y rounded-md border'>
            {list.data.items.map(def => (
              <LibraryFieldItem
                key={def.id}
                def={def}
                onPick={d => handlePickItem(d)}
                pickableFields={pickableFields}
                expanded={expandedIds.has(def.id)}
                onToggleExpand={toggleExpanded}
                stepLabel={selectedStep?.label}
                insertedInto={lastInsertedInto.get(def.id)}
                error={rowErrors[def.id]}
                confirming={confirmingId === def.id}
                onConfirmAgain={() => handlePickItem(def, true)}
                onCancelAgain={() => setConfirmingId(null)}
              />
            ))}
          </ul>
        )}

        {total > 0 && (
          <div className='flex items-center justify-between pt-1 text-xs text-muted-foreground'>
            <span>
              {Math.min(skip + 1, total)}–{Math.min(skip + PICKER_TAKE, total)} of {total}
            </span>
            <div className='flex gap-1'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={skip === 0}
                onClick={() => setSkip(s => Math.max(0, s - PICKER_TAKE))}
              >
                Prev
              </Button>
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={skip + PICKER_TAKE >= total}
                onClick={() => setSkip(s => s + PICKER_TAKE)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* This session's inserts, in click order. */}
      {inserted.length > 0 && !collapsed && (
        <div className='shrink-0 border-t bg-muted/30 px-3 py-2'>
          <p className='mb-1 text-xs font-medium'>
            {inserted.length} {inserted.length === 1 ? 'field' : 'fields'} inserted
          </p>
          <ol aria-label='Inserted this session' className='max-h-24 space-y-0.5 overflow-y-auto text-[11px] text-muted-foreground'>
            {inserted.map(r => (
              <li key={r.key} className='truncate'>
                <span className='text-foreground'>{r.label}</span> → {r.section}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
    {remapDialog}
    </>
  )
}

/**
 * Another version of the form, opened read-only from the version indicator
 * (z8nrz7dmb1 AC2.4–AC2.6).
 *
 * Rendered by the same FormRenderer the builder's Preview uses, locked: every
 * field, section, layout rule and piece of logic renders, and nothing that
 * could change the form exists here — no canvas, palette, properties panel or
 * Save. A banner names the version and says it is read-only, and one control
 * returns to the editable version. Reading it writes nothing (AC2.9).
 */
function ReadOnlyVersionView({
  versionId,
  indicator,
  returnLabel,
  onReturn,
  backTo,
  tenantTimezone,
}: {
  versionId: string
  indicator: React.ReactNode
  returnLabel: string
  onReturn: () => void
  backTo: { to: string; label: string } | null
  tenantTimezone?: string
}) {
  const version = useQuery({
    queryKey: ['form-definition', versionId],
    queryFn: () => formsApi.getDefinition(versionId),
  })
  const def = version.data
  const [submission, setSubmission] = useState<FormSubmission>(() => ({
    ...makePreviewSubmission(),
    // Completed = locked: the renderer disables every field and drops Complete Form.
    status: 'completed',
  }))
  const uniqueId = def ? formUniqueId(def) : null

  return (
    <div className='flex h-full flex-col overflow-hidden bg-background'>
      <header className='flex h-14 shrink-0 items-center gap-3 overflow-x-auto border-b px-4'>
        <Link
          to={backTo?.to ?? '/form-builder?tab=forms'}
          className='shrink-0 text-sm text-muted-foreground transition-colors hover:text-foreground'
        >
          ← {backTo?.label ?? 'Form Builder'}
        </Link>
        <div className='h-4 w-px shrink-0 bg-border' />
        <span className='min-w-40 flex-1 truncate text-sm font-semibold'>{def?.name ?? 'Loading…'}</span>
        {indicator}
        <Button size='sm' variant='outline' onClick={onReturn}>
          <ArrowLeft className='mr-1.5 h-3.5 w-3.5' />
          Return to {returnLabel}
        </Button>
      </header>
      <div className='flex-1 overflow-y-auto bg-muted/30'>
        <div className={cn('py-8', FORM_PAGE_GUTTER_CLASS)}>
          {version.isError ? (
            <p className='text-sm text-destructive'>This version could not be loaded.</p>
          ) : !def ? (
            <p className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' /> Loading version…
            </p>
          ) : (
            <>
              <div
                role='status'
                data-testid='read-only-version-banner'
                className='mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900'
              >
                <Eye className='h-3.5 w-3.5 shrink-0' />
                <span>
                  You are viewing <span className='font-mono font-semibold'>{uniqueId}</span>
                </span>
                <FormLifecycleBadge active={def.active} />
                <span>— read-only. Nothing here can be edited or saved.</span>
              </div>
              <div className='rounded-xl border bg-card p-6 shadow-sm'>
                <FormRenderer
                  definition={def}
                  submission={submission}
                  currentUser={PREVIEW_USER}
                  onSubmissionUpdated={setSubmission}
                  preview
                  tenantTimezone={tenantTimezone}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
